// Pure, dependency-free geometry helpers for OUTER LABEL-PILL collision
// resolution on the wheel renderer (createPie.js `updateLable`).
//
// Why this exists: every outer label pill is anchored at the same radius
// (~1.2 * pieOutterRadius) at its segment's mid-angle. On a many-segment wheel
// rendered small (e.g. an 18-segment wheel at 375px mobile), adjacent pills
// pack tightly and overlap/occlude one another (Wave-5 QA: "Segment 17" hidden
// behind 16/18 at 12 o'clock). createPie measures each rendered pill's box and
// asks this module which pills can stay, degrading the rest full -> dot (the
// adaptive-ladder law from the deck's adaptive-icons-elevated §07, applied to
// labels — but keyed on MEASURED overlap because a pill's footprint depends on
// its text width, not the wheel diameter alone). ISP-9999 (Gordon 2026-08-31):
// the ladder used to have a third "hidden" rung when even a dot had no clear
// room; that let a segment's pill disappear entirely (wheel 2642), so it was
// removed — every segment always keeps at least a dot, rendered OVERLAPPED if
// necessary, never hidden. See resolveLabelOverlaps (createPie.js) /
// resolvePillOverlaps (renderer-v2/engine.js) for the paint-order rule this
// implies (earlier clockwise segment on top).
//
// Everything here is pure rectangle math so it is unit-testable without d3 or a
// DOM. A "rect" is any object with numeric { left, top, right, bottom } (a
// browser DOMRect satisfies this).

// ── Full-circle (single-segment) wedge detection (SINGLE SOURCE OF TRUTH) ───
// fix-daniyal-should-come-center-when-segment-only-one-web: a wheel's only
// segment spans the full circle (startAngle 0, endAngle 2π). Every rim-pill
// anchor below is keyed on the wedge's MID-angle, but a full-circle wedge has
// no real direction — its midAngle is still some arbitrary point (π, straight
// down for a D3/v2 pie layout), which rim-hugged the lone pill to the bottom
// of an otherwise featureless circle instead of the wheel's center. MEASURED
// live on web-stg.iyuga.net (wheel 507143, bundle main.6d5c0152.js): a
// 1-segment wheel's pill rendered at `translate(~0, outerRadius·1.05+rimGap)`,
// stranded below the ring. Both engines import this same check so a wedge
// counts as "full circle" identically by construction:
//   · D3Wheels/createPie.js   (v1 engine — updateLable pill transform)
//   · renderer-v2/geometry.js (v2 engine — pillLabelAnchor / labelAnchor)
export const FULL_CIRCLE_EPSILON = 1e-6;

export function isFullCircleWedge(startAngle, endAngle) {
  return endAngle - startAngle >= Math.PI * 2 - FULL_CIRCLE_EPSILON;
}

// ── Overlap-degradation ladder tuning (SINGLE SOURCE OF TRUTH) ───────────────
// The two constants that key the full-pill -> dot -> hidden ladder. They live
// HERE (not in a renderer) so BOTH wheel engines import the identical values and
// their ladders engage at the same density by construction:
//   · D3Wheels/createPie.js  (v1 engine — resolveLabelOverlaps / demoteLabelToDot)
//   · renderer-v2/engine.js  (v2 engine — resolvePillOverlaps / demotePillToDot)
// Moving them here is behaviour-neutral (the values are unchanged from v1's
// original createPie.js definitions) and removes the drift risk of two copies.

// Two pills that sit within this many px of each other count as colliding.
// ISP-9999 (Gordon 2026-08-12): the rim-pill ladder must PRIORITIZE each pill
// staying ANCHORED to its own segment over inter-pill spacing. A positive gap
// demanded breathing room and made the resolver eager to move/degrade a pill
// that was merely near a neighbour — which fed the outward drift (below). We
// reduce the minimum gap to 0 so adjacent pills may sit right up against each
// other (touching) before they even count as colliding — "reduce the minimum
// margin/gap between adjacent title pills." Both engines import this, so the
// density at which the ladder engages stays identical across engines.
export const LABEL_PILL_GAP_PX = 0;

// Radius (px) of the small colored "dot" a pill degrades to when it has no room
// for the full pill but the wheel is not yet too dense to show a marker.
export const LABEL_DOT_RADIUS_PX = 4;

// ── Label text truncation (SINGLE SOURCE OF TRUTH) ───────────────────────────
// Max characters shown in a rendered Segment-title label before it is truncated
// with a trailing ellipsis. Ported verbatim from the v1 D3 engine
// (createPie.js: `this.segmentLableLimit = config?.segmentLableLimit || 25`),
// which caps every rim-pill / label text at 25 chars + "..." so a long title
// can never run past the viewport edge on a space-constrained (dense / narrow)
// wheel. It lives HERE — beside the shared ladder constants — so BOTH wheel
// engines import the identical limit and truncate at the same length by
// construction (renderer-v2/engine.js was missing this, so long v2 labels
// overflowed the viewport; RENDERER-V2-REAL-DIFF R1).
export const SEGMENT_LABEL_CHAR_LIMIT = 25;

// Truncate a label to `limit` chars + "..." — v1 parity, exactly
// `title.length > limit ? title.slice(0, limit) + "..." : title`. Pure (no DOM)
// so it is unit-testable in isolation. Nullish / non-string input returns "".
export function truncateLabel(title, limit = SEGMENT_LABEL_CHAR_LIMIT) {
  const s = title == null ? "" : String(title);
  return s.length > limit ? s.slice(0, limit) + "..." : s;
}

// GVW-P02: middle-ellipsis truncation. Same total visible-character budget as
// truncateLabel (limit + "..."), so it doesn't reopen the 390px off-screen bug
// PHONE_LABEL_LIMIT was set to fix — it just spends that budget on a head AND
// a tail instead of an all-head cap, so same-prefix names ("Segment Alpha" /
// "Segment Beta") stay distinguishable instead of both collapsing to
// "Segment...".
export function truncateLabelMiddle(title, limit = SEGMENT_LABEL_CHAR_LIMIT) {
  const s = title == null ? "" : String(title);
  if (s.length <= limit) return s;
  const head = Math.ceil(limit / 2);
  const tail = Math.floor(limit / 2);
  return s.slice(0, head) + "..." + s.slice(s.length - tail);
}

// T22.188 (Gordon decision 2026-10-13, pairs with T22.176): the phone View
// Wheel shows a long segment title on ONE line cut off with "..." again
// instead of wrapping it into a tall multi-line pill (MEASURED live web-stg
// main.5c953e00 /wheel/507029 @390: 7-char wrap -> 8-line ~150px pills).
// Grapheme-safe (an emoji/flag is never split in half) and trims the space a
// word-boundary cut would leave before the ellipsis ("Capital..." not
// "Capital ..."). Always returns exactly one line; a title within the limit
// is returned untouched.
export function truncateLabelEnd(title, limit = SEGMENT_LABEL_CHAR_LIMIT) {
  const s = title == null ? "" : String(title);
  const units = graphemeUnits(s);
  const max = Math.max(1, Math.floor(limit) || 1);
  if (units.length <= max) return s;
  return units.slice(0, max).join("").replace(/\s+$/, "") + "...";
}

// ── Label WRAPPING (SINGLE SOURCE OF TRUTH — both engines) ──────────────────
// gv-0914c (Gordon video #3, "always full titles"): an all-head ellipsis
// truncation clips a title no matter how much room the surface actually has —
// no window width can ever satisfy "show the whole title" through truncation
// alone. Word-wrap it across as many lines as it takes instead: each line
// stays within the same per-position budget the caller's char-limit already
// computed (so a line can never clip past its pane edge, the same guarantee
// a truncation gave), but the text is NEVER CUT — no ellipsis, ever, at any
// length (createPie.segmentTitleNeverEllipsis.test.js proves this for
// titles up to 68 chars). Deliberately UNBOUNDED in line count — see that
// test's own "wraps onto more lines on the narrow canvas" assertion for why
// a hard line cap is out of scope here: it would reintroduce ellipsis
// truncation for long titles, the exact regression this function exists to
// prevent. (fix-vy0918d-rimpill-ellipsis-truncates-view-page PR body has a
// WORDING/DESIGN-FOR-GORDON note on this — the bug's own dispatch note
// paraphrased "FE #5546's intent" as "no unbounded wrapping past two lines",
// which does not match what #5546 actually shipped/tests.)
// Originally a private, duplicated pair of helpers inside v1's createPie.js —
// moved here (fix-vy0918d-rimpill-ellipsis-truncates-view-page) so BOTH wheel
// engines (v1 createPie.js, v2 renderer-v2/engine.js) wrap identically instead
// of only v1 wrapping while v2 still hard-truncated with "...".
//
// T22.352 (Gordon decision 2026-10-16, MEASURED live web-stg main.cb684d72
// /wheel/508734 @1440): a greedy wrap at exactly the budget left
// "Proofing, Shaping, and / Baking Your Sourdough / Loaf" — a single orphan
// word on the last line. The budget may now flex by a SMALL band
// (PILL_WRAP_FLEX_RATIO, a few % either side of the default max width) and
// the width inside that band that wraps into the tidiest block wins: no
// hard mid-word break if avoidable, no single-word last line, fewest lines,
// then the most even line lengths, then the width closest to the default
// (so a title that already wraps well keeps its old breaks). Never more
// than the band — pill sizes stay visually consistent across the wheel.
export const PILL_WRAP_FLEX_RATIO = 0.08;

export function wrapLabelIntoLines(str, maxUnitsPerLine) {
  const s = str == null ? "" : String(str);
  const limit = Math.max(1, Math.floor(maxUnitsPerLine) || 1);
  const units = graphemeUnits(s);
  if (units.length <= limit) return [s];
  const flex = Math.max(1, Math.ceil(limit * PILL_WRAP_FLEX_RATIO));
  let best = null;
  for (let w = Math.max(1, limit - flex); w <= limit + flex; w++) {
    const { lines, hardBreaks } = greedyWrapUnits(units, w);
    const lens = lines.map((l) => graphemeUnits(l).length);
    const last = lines[lines.length - 1];
    const score = [
      hardBreaks,
      lines.length > 1 && !/\s/.test(last.trim()) && /\s/.test(s.trim()) ? 1 : 0,
      lines.length,
      Math.max(...lens) - Math.min(...lens),
      Math.abs(w - limit),
      w > limit ? 1 : 0,
    ];
    if (!best || compareScores(score, best.score) < 0) best = { lines, score };
  }
  return best.lines;
}

function compareScores(a, b) {
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return a[i] - b[i];
  return 0;
}

// The original greedy wrap at a fixed budget (pre-T22.352 behaviour, still
// used per candidate width above). `hardBreaks` counts mid-word breaks.
function greedyWrapUnits(unitsIn, limit) {
  let units = unitsIn;
  let hardBreaks = 0;
  const lines = [];
  while (units.length > limit) {
    // Prefer breaking at the last space within budget so words stay whole;
    // fall back to a hard break at the budget so a single overlong word
    // still makes progress (never drops text — no ellipsis, ever).
    let breakAt = limit;
    for (let i = Math.min(limit, units.length - 1); i > 0; i--) {
      if (units[i] === " ") {
        breakAt = i;
        break;
      }
    }
    // fix-kelly-u09gjpu7ysx-usman-had-me-delete-native-app-mobile: a hard break
    // (no space found in budget) that leaves only a 1-2 unit tail before the
    // next space/end-of-string strands that tail as its own near-empty line —
    // MEASURED live on web-stg.iyuga.net (wheel 507140, 390px): "Shipping"
    // rendered as tspans "Shippin" / "g", "Exclusive" as "Exclusi" / "ve".
    // Folding that short tail into the current line (accepting a couple units
    // of overflow past the budget) reads as a clean wrap instead of a broken
    // orphan line.
    if (units[breakAt] !== " ") {
      let wordEnd = breakAt;
      while (wordEnd < units.length && units[wordEnd] !== " ") wordEnd++;
      if (wordEnd - breakAt <= 2) breakAt = wordEnd;
    }
    if (units[breakAt] !== " ") hardBreaks++;
    lines.push(units.slice(0, breakAt).join(""));
    units = units.slice(breakAt);
    if (units[0] === " ") units = units.slice(1);
  }
  if (units.length) lines.push(units.join(""));
  return { lines, hardBreaks };
}

// T22.176 (Gordon 2026-10-13, "Responsive Pages look like a glitchy UI (not
// RN)"): a phone surface may cap the wrapped lines. MEASURED live web-stg
// main.e6ad20ae.js @390x844: the 7-char unbounded wrap stacked a long title
// into 13 one-word lines (191px tall pills over the wheel and under the
// floating buttons). The RN app shows a phone title on ONE line with "..."
// (iyuga-react-native wheelLabelLayout.ts truncateSegmentLabel); the phone
// View Wheel keeps up to `maxLines` lines and ends the last kept line with
// "..." — the full title stays in the wedge's aria-label and the segment view.
// Opt-in only: maxLines undefined / <= 0 returns `lines` untouched, so every
// surface that does not pass it keeps the unbounded "never cut" wrap above.
export function clampLabelLines(lines, maxLines, maxUnitsPerLine, title) {
  const cap = Math.floor(maxLines);
  if (!Array.isArray(lines) || !(cap > 0)) return lines;
  const limit = Math.max(1, Math.floor(maxUnitsPerLine) || 1);
  // A capped (phone) pill never splits a WORD across lines ("Vegetar|ian",
  // "AuditTy|pedpr" — MEASURED on narrow side rays): when the wrap had to
  // hard-break inside a word, show ONE line with "..." instead (RN parity).
  if (title != null && lines.length > 1) {
    const s = String(title);
    let at = 0;
    let midWord = false;
    for (let i = 0; i < lines.length - 1 && !midWord; i++) {
      const found = s.indexOf(lines[i], at);
      if (found < 0) break;
      at = found + lines[i].length;
      if (at < s.length && s[at] !== " " && s[at - 1] !== " ") midWord = true;
    }
    // T22.352's flex wrap keeps an over-long word whole instead of
    // hard-breaking it ("Vegetarian" on a 7-char line) - a kept line wider
    // than the flex band is the same "word does not fit" case.
    const band = limit + Math.max(1, Math.ceil(limit * PILL_WRAP_FLEX_RATIO));
    if (!midWord && lines.slice(0, cap).some((l) => graphemeUnits(l).length > band)) midWord = true;
    if (midWord) {
      const units = graphemeUnits(s.trim()).slice(0, Math.max(1, limit - 1));
      return [units.join("").replace(/[\s.,;:!?-]+$/, "") + "..."];
    }
  }
  if (lines.length <= cap) return lines;
  const kept = lines.slice(0, cap);
  // The last kept line is refilled from the REST of the title (not just its
  // own wrapped line), so it reads "Connections: Ma..." instead of a lone
  // word — the line is as full as the budget allows, like RN's single line.
  let rest = kept[cap - 1];
  if (title != null) {
    const s = String(title);
    let at = 0;
    for (let i = 0; i < cap - 1; i++) {
      const found = s.indexOf(kept[i], at);
      if (found < 0) { at = -1; break; }
      at = found + kept[i].length;
    }
    if (at >= 0) rest = s.slice(at).replace(/^\s+/, "");
  }
  let units = graphemeUnits(rest).slice(0, Math.max(1, limit - 1));
  // Prefer ending on a word boundary when that keeps most of the line.
  const lastSpace = units.lastIndexOf(" ");
  if (lastSpace >= Math.ceil(limit * 0.6)) units = units.slice(0, lastSpace);
  kept[cap - 1] = units.join("").replace(/[\s.,;:!?-]+$/, "") + "...";
  return kept;
}

// gv-0914c: renders a label's wrapped lines as stacked <tspan>s, replacing
// any prior render (upsert-safe for hover/selection re-layout). `x=0` on
// every tspan re-anchors each line to the text element's own text-anchor
// (start/middle/end) instead of inheriting the previous line's end position.
// Vertical placement keeps the existing single-line baseline convention
// (dy="0.35em" from the anchor) byte-identical when there's exactly one
// line, and centers the whole block on the anchor when there's more than one.
export const LABEL_LINE_HEIGHT_EM = 1.15;

export function renderLabelLines(textNode, lines) {
  while (textNode.firstChild) textNode.removeChild(textNode.firstChild);
  const n = lines.length;
  lines.forEach((line, i) => {
    const tspan = document.createElementNS("http://www.w3.org/2000/svg", "tspan");
    tspan.setAttribute("x", 0);
    tspan.setAttribute(
      "dy",
      i === 0
        ? `${0.35 - ((n - 1) * LABEL_LINE_HEIGHT_EM) / 2}em`
        : `${LABEL_LINE_HEIGHT_EM}em`
    );
    tspan.textContent = line;
    textNode.appendChild(tspan);
  });
}

// ── Middle-truncation COLLISION AVOIDANCE (SINGLE SOURCE OF TRUTH) ──────────
// ISP-9999 rs-0914-dev-segment-labels-truncate-10ch-390 (PR #5497): two
// DIFFERENT segment titles can share both a common prefix AND a common suffix
// within the head+tail budget and so collapse to the IDENTICAL truncated
// string — MEASURED live (web-dev.iyuga.net, wheel 6232, phone limit 7):
// "Segment 1" and "Segment 1Segment 1" both rendered "Segm...t 1". This fix
// originally landed ONLY inside the v1 D3 engine (createPie.js), as private
// module-scope helpers never exported here — so the v2 engine (this file's
// other caller, renderer-v2/engine.js) kept the bug (fix-fe-5497-rimpill-
// collision-viewwheel-regression-0917). Moved here so BOTH engines share the
// identical disambiguation policy by construction, same pattern as the
// overlap-ladder constants above.
//
// graphemeUnits: split by GRAPHEME (Intl.Segmenter) so an emoji/flag/ZWJ
// sequence counts as one visible unit; falls back to code-points
// (Array.from, still surrogate-pair safe) where Segmenter is unavailable.
export function graphemeUnits(str) {
  const s = str == null ? "" : String(str);
  try {
    if (typeof Intl !== "undefined" && typeof Intl.Segmenter === "function") {
      return Array.from(
        new Intl.Segmenter(undefined, { granularity: "grapheme" }).segment(s),
        (seg) => seg.segment
      );
    }
    return Array.from(s);
  } catch (e) {
    return Array.from(s);
  }
}

// Given a title whose middle-truncated candidate is already `taken` by an
// earlier, DIFFERENTLY-titled label, try the other (head, tail) splits that
// spend the SAME total visible-unit budget (so the pill can never widen past
// what its caller's limit already bounded) until one isn't in `taken`.
// Pure + grapheme-safe. Falls back to the original (default-split) candidate
// — still a collision — only when no split can tell the pair apart.
export function disambiguateMiddleTruncation(title, limit, taken) {
  const s = title == null ? "" : String(title);
  const units = graphemeUnits(s);
  if (units.length <= limit) return s;
  const defaultHead = Math.ceil(limit / 2);
  const defaultTail = Math.floor(limit / 2);
  const candidateFor = (head, tail) =>
    tail > 0
      ? units.slice(0, head).join("") + "..." + units.slice(units.length - tail).join("")
      : units.slice(0, head).join("") + "...";
  const splits = [[defaultHead, defaultTail]];
  for (let head = limit; head >= 0; head--) {
    if (head === defaultHead) continue;
    splits.push([head, limit - head]);
  }
  for (const [head, tail] of splits) {
    const candidate = candidateFor(head, tail);
    if (!taken.has(candidate)) return candidate;
  }
  return candidateFor(defaultHead, defaultTail);
}

// Do two axis-aligned rectangles overlap? An optional `gap` (px) inflates each
// box so pills that merely touch (or sit within `gap` of each other) count as
// colliding — labels need a little breathing room, not just non-intersection.
export function rectsOverlap(a, b, gap = 0) {
  if (!a || !b) return false;
  return (
    a.left < b.right + gap &&
    a.right > b.left - gap &&
    a.top < b.bottom + gap &&
    a.bottom > b.top - gap
  );
}

// Greedy placement: walk `candidates` (each { id, rect }) in the given priority
// order and keep a candidate only if its rect does not collide (within `gap`)
// with any already-kept rect. Returns { kept: Set<id>, rejected: Set<id> } plus
// the list of placed rects so a caller can run a second pass (e.g. dots) against
// the same accumulator. Deterministic: earlier candidates win ties.
export function greedyPlace(candidates, gap = 0, seedRects = []) {
  const placed = seedRects.slice();
  const kept = new Set();
  const rejected = new Set();
  for (const c of candidates || []) {
    if (!c || !c.rect) {
      rejected.add(c && c.id);
      continue;
    }
    const collides = placed.some((p) => rectsOverlap(c.rect, p, gap));
    if (collides) {
      rejected.add(c.id);
    } else {
      placed.push(c.rect);
      kept.add(c.id);
    }
  }
  return { kept, rejected, placed };
}

// A rect is "measurable" when it has real, non-zero size. A display:none /
// unrendered node yields a zero box (and must be ignored, not treated as a
// point collider).
export function isMeasurableRect(rect) {
  return (
    !!rect &&
    typeof rect.width === "number" &&
    typeof rect.height === "number" &&
    rect.width > 0 &&
    rect.height > 0
  );
}

// ── Keep-anchored overlap tolerance (SINGLE SOURCE OF TRUTH — both engines) ──
// ISP-9999 (Gordon 2026-08-12): "prioritize each pill staying ANCHORED/aligned
// to its own segment over inter-pill spacing … even allow a little OVERLAP
// rather than let a pill drift away from its segment. The collision resolver
// should give up spacing before it gives up alignment." So when a full pill
// collides at its rim anchor we now PREFER leaving it exactly where it is (at
// its segment's angular centre) and accepting a bounded overlap, instead of
// nudging it radially outward (which sacrificed alignment — the reported bug:
// the bottom-cluster pills drifted 33–80px off the rim). We only stop keeping a
// pill "full" once a neighbour would occlude more than this fraction of its box,
// at which point it degrades to a compact dot AT ITS ANCHOR — never a drift.
// 0.45 ⇒ up to ~45% of a pill may be covered and it still reads (the leading
// text edge stays visible); past that it is demoted so labels never fully
// occlude one another.
//
// ISP-9999 (Gordon 2026-08-28): in production a bounded overlap still let two
// truncated pills render text-on-text (QA-measured on wheel 2642: "Customer
// Success & Suppor..." / "QAsave557763" overlapped 108x11px) — 45% coverage of
// a SMALL neighbour's box can still be most of that neighbour, because the
// ratio is checked against the CANDIDATE pill's own area, not the pill it
// lands on top of. Both resolvers (createPie.js resolveLabelOverlaps,
// renderer-v2/engine.js resolvePillOverlaps) now call `canKeepAnchored(rect,
// placed, 0)` — an explicit 0 override, not this default — so a full/dot pill
// keeps its anchor only when it has ZERO overlap with every already-placed
// rect; any actual overlap degrades it (still AT its own anchor, never a
// drift). This constant stays the exported default for canKeepAnchored's
// generic (non-zero-tolerance) use, and for its own unit tests below.
export const LABEL_KEEP_MAX_OVERLAP_RATIO = 0.45;

// Area of the intersection of two axis-aligned rects (0 when disjoint). Pure.
export function intersectionArea(a, b) {
  if (!a || !b) return 0;
  const w = Math.min(a.right, b.right) - Math.max(a.left, b.left);
  const h = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
  return w > 0 && h > 0 ? w * h : 0;
}

// Fraction of `target`'s own area covered by the MOST-overlapping rect in
// `others` (0 = clear, 1 = fully covered). This is the readability metric the
// keep-anchored decision keys on: how much of THIS pill a neighbour hides.
export function coveredRatio(target, others) {
  if (!target) return 0;
  const area =
    (target.width != null ? target.width : target.right - target.left) *
    (target.height != null ? target.height : target.bottom - target.top);
  if (!(area > 0)) return 0;
  let maxCov = 0;
  for (const o of others || []) {
    const cov = intersectionArea(target, o) / area;
    if (cov > maxCov) maxCov = cov;
  }
  return maxCov;
}

// May a colliding full pill STAY at its segment anchor (accepting the overlap)
// instead of being moved/degraded? Yes while no neighbour covers more than
// `maxRatio` of it — alignment is preferred over spacing up to that readability
// bound. Pure + shared so both engines make the identical call.
export function canKeepAnchored(
  full,
  placed,
  maxRatio = LABEL_KEEP_MAX_OVERLAP_RATIO
) {
  return coveredRatio(full, placed) <= maxRatio;
}

// ── Label-vs-WHEEL collision (SINGLE SOURCE OF TRUTH — both engines) ────────
// gordon-0920-view-wheel-segment-title-labels-must-never-overlap-the-wheel:
// owner report + design law (rim-pill-never-hide-clockwise-overlap decision
// log) — a segment title pill MAY overlap ANOTHER pill (see the
// keep-anchored law above) but must NEVER cover any pixel of the wheel
// itself (outer rim / any wedge / the portrait). MEASURED on wheel 4908
// (1920x1080, web-dev): several multi-line pills (e.g. "Exploring Modern and
// Contemporary Furniture Designs") painted their rect OVER the outer ring.
//
// ROOT CAUSE (both engines, byte-identical bug): the pill's anchor point was
// placed at a FIXED radius (`outerRadius*1.05 + rimGap`) along the segment's
// bisector — a constant that only reflects the GAP, never the pill's own
// rendered size. A pill's background <rect> is centered on that anchor
// vertically (renderLabelLines' dy centering) and only offset horizontally by
// its text-anchor, so a tall (multi-line) or wide pill's near edge/corner can
// sit CLOSER to the wheel's centre than the anchor itself — inside the fixed
// gap, sometimes inside the ring.
//
// FIX: a pill's anchor must be pushed out by the pill's own SUPPORT distance
// (the standard "furthest extent of an axis-aligned box, in a given
// direction, from its own centre") in the radial direction, not just the
// flat gap. For a box with half-width `halfWidth` and half-height
// `halfHeight`, centred at a point `radius` out along direction
// (sin(midAngle), -cos(midAngle)) [this module's/geometry.js's polar
// convention: 0 = 12 o'clock, clockwise], the box's support in that exact
// direction is `halfWidth*|sin(midAngle)| + halfHeight*|cos(midAngle)|`.
// Placing the box centre at `minRadius + support` guarantees EVERY point in
// the box is at distance >= minRadius from the origin (proof: the box lies
// entirely in the half-plane {P : P·d >= centre·d - support}, and
// |P| >= P·d since d is a unit vector) — this holds for ANY angle and ANY
// box size, which is what pillSafeAnchorRadius.geometry.test.js asserts
// exhaustively (the prove-can-fail law: a pill placed 10px short of this
// radius must measure inside minRadius, i.e. FAIL).
//
// `minRadius` is the caller's `outerRadius + gap` (the popped/selected outer
// radius when the wedge is popped out — callers must pass the RENDERED
// radius, not the resting one, so a selected wedge's grown rim still gets
// its labels pushed out to match).
export function pillSafeAnchorRadius(minRadius, midAngle, halfWidth, halfHeight) {
  const dx = Math.abs(Math.sin(midAngle));
  const dy = Math.abs(Math.cos(midAngle));
  const support = Math.max(0, halfWidth) * dx + Math.max(0, halfHeight) * dy;
  return Math.max(0, minRadius) + support;
}

// Guaranteed LOWER BOUND on a box's distance from the origin, given the box
// is centred at `anchorRadius` out along `midAngle` with the half-extents
// above. Exists purely so both the fix and its regression test can compute
// the same "worst case nearest point" number — used by
// pillSafeAnchorRadius.geometry.test.js's positive control (moving a label
// 10px inward must measure BELOW the design gap).
export function pillWorstCaseDistance(anchorRadius, midAngle, halfWidth, halfHeight) {
  const dx = Math.abs(Math.sin(midAngle));
  const dy = Math.abs(Math.cos(midAngle));
  const support = Math.max(0, halfWidth) * dx + Math.max(0, halfHeight) * dy;
  return anchorRadius - support;
}

// ── Font-ready re-place (gordon-0920-labels-firefox) ─────────────────────────
// pillSafeAnchorRadius's guarantee is only as good as the halfWidth/halfHeight
// it is fed, and BOTH engines source those from a `text` node's LIVE
// `getBBox()` (createPie.js loadRectSize / renderer-v2/engine.js renderPill),
// taken synchronously on first draw. If the DM Sans web font has not finished
// loading at that instant, the browser measures the fallback font's box
// instead — a different width/height per engine's fallback-font metrics — and
// once DM Sans swaps in, the PAINTED text no longer matches the box the
// push-out radius was computed from. Measured live on web-dev.iyuga.net
// (main.3795c4fd.js, Firefox, 1440w, /wheel/4908): 7 of 8 pills landed 3-7.3px
// INSIDE the required 8px rim gap — Chromium/WebKit happened to already have
// DM Sans warm (cached from an earlier same-origin load in this harness), so
// only Firefox's cold-cache timing exposed it. This is a timing race, not a
// per-browser metric difference, so the fix is one shared re-place hook, not
// a per-engine fork.
//
// `doc.fonts.ready` resolves once the fonts needed for the CURRENT layout are
// loaded, but a font that was not yet requested at first paint (e.g. this
// component mounting before anything else on the page needed DM Sans) can
// still finish loading AFTER `ready` already resolved once; `loadingdone`
// fires for that case too, so both are wired — `relayout` is idempotent
// (it's just "measure + place again"), so an extra call is harmless.
// Returns an unsubscribe function; always safe to call (no-op on a doc with no
// FontFaceSet, e.g. jsdom).
export function scheduleFontReadyRelayout(doc, relayout) {
  const fonts = doc && doc.fonts;
  if (!fonts || typeof relayout !== "function") return () => {};
  let destroyed = false;
  const run = () => {
    if (!destroyed) relayout();
  };
  if (typeof fonts.ready === "object" && typeof fonts.ready.then === "function") {
    fonts.ready.then(run, () => {});
  }
  const hasListener = typeof fonts.addEventListener === "function";
  if (hasListener) fonts.addEventListener("loadingdone", run);
  return () => {
    destroyed = true;
    if (hasListener) fonts.removeEventListener("loadingdone", run);
  };
}

// ── Settle re-verification (gordon-0928-P0-rim-labels-wrong-on-first-load-
// until-hover) ────────────────────────────────────────────────────────────
// MEASURED LIVE (web-dev.iyuga.net, /builder/4908, headless Chromium, cold
// load, ZERO pointer events): every upstream trigger above (a resize tick,
// the font-ready hook) runs its OWN single relayout pass and never checks its
// own result again. A MutationObserver on every rim-pill's `transform`
// attribute, watched across the whole cold-load sequence, caught the actual
// failure: 6 of 8 pills converged to a stable, ring-safe transform by ~5.3s
// and never moved again; the remaining 2 ("RegressionTitle-89177" and
// "Exploring Modern and Contemporary Furniture Designs" — matching the
// reporter's own two overlapping pills exactly) kept moving through several
// ticks and then, on their OWN final relayout pass, landed on a transform
// 45-52px SHORT of where their two PRIOR passes had already safely placed
// them — a regression, not a not-yet-converged state — and nothing ever
// re-measured them again: the deficit (measured -45.3px vs the required
// +8px gap) was still there, unchanged, 14+ seconds later. Two mouse moves
// across the wheel afterward (a real hover) changed nothing either — moving
// the mouse is not what actually fixes this on a live page; further wall-
// clock settling is.
//
// Root-causing the EXACT one-tick regression (which upstream measurement —
// pane width, font metrics, or something else — went stale on that specific
// pass) needs deeper live instrumentation than a black-box measurement can
// prove. But the FIX doesn't need the exact cause: `relayoutLabelPills()` is
// idempotent and self-correcting BY CONSTRUCTION (loadRectSize's own ring-
// safety loop re-measures the real, current DOM box every time it runs) —
// the only thing missing is a LATER call once the geometry has genuinely
// stopped changing. These are bounded, cheap (idempotent no-op when already
// correct) extra passes scheduled after every trigger that can legitimately
// change label geometry (a resize settle, a font-ready signal), so a single
// bad pass is never the last word.
export const RELAYOUT_SETTLE_DELAYS_MS = [300, 1200];

// ── FINAL RING-SAFETY CORRECTION (SINGLE SOURCE OF TRUTH — both engines) ────
// gordon-0920-EDIT-WHEEL-builder-labels-ROUND-2 (an independent breaker unit's
// repeat measurement AFTER FE #5899/#5895 both merged and deployed): every
// upstream placement step (pillSafeAnchorRadius's push-out above, the
// Builder's own pane-width clamp in labelRadialX.js) reasons in SVG-LOCAL
// units from an ESTIMATED box (loadRectSize / renderPill's own padded
// getBBox() rect), and assumes nothing else moves the box afterward and that
// local units map 1:1 onto on-screen page pixels. Two ways that broke, both
// measured live on the POST-FIX bundle (main.558f6ebc.js):
//   (1) Builder's clampAnchorTxToPane / clampPillBoxToPane can nudge a pill's
//       box BACK toward the pane centre after the ring-safety push-out
//       already ran, silently undoing it — measured: /builder LEFT-half
//       labels landing 20-43px INSIDE the required 8px rim gap, with
//       IDENTICAL numbers run after run (not a timing race — deterministic
//       geometry: the clamp fires every render, every time).
//   (2) A wheel engine's own text metrics for the SAME font differ a few px
//       per browser (Firefox vs Chromium getBBox()), so the push-out's own
//       halfWidth/halfHeight input is already slightly wrong on some engines
//       even before any clamp runs — measured: /wheel (View Wheel, no pane
//       clamp at all) Firefox labels landing 3.2-3.4px short of the required
//       8px at both 1440 and 1024, Chromium clean (8.1px) on the identical
//       wheel/titles.
// Rather than chase each upstream cause separately (and re-break the same
// invariant the next time a caller adds one more transform), this runs LAST,
// after every other placement step, and re-measures the box's REAL on-screen
// position via getBoundingClientRect() — the exact API + coordinate space the
// owner's own acceptance ruler uses — so it is correct regardless of which
// upstream step (or a future one) under- or over-shot. Both engines call this
// with their OWN live-rendered rects; never a synthetic/estimated box.

// Union bounding box (page-pixel space) of a set of already-rendered DOM
// elements, reduced to a {center, radius} pair — same "wedges union to the
// full circle's own bounding square" identity the owner's ruler uses, so a
// caller never needs a separate "find the ring" selector. Returns null when
// `elements` is empty/unmeasurable (nothing to correct against).
export function unionBBoxCenterRadius(elements) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const el of elements || []) {
    const r = el && typeof el.getBoundingClientRect === "function" ? el.getBoundingClientRect() : null;
    if (!r || !(r.width > 0 || r.height > 0)) continue;
    minX = Math.min(minX, r.left);
    minY = Math.min(minY, r.top);
    maxX = Math.max(maxX, r.right);
    maxY = Math.max(maxY, r.bottom);
  }
  if (!(maxX > minX) || !(maxY > minY)) return null;
  return {
    center: { x: (minX + maxX) / 2, y: (minY + maxY) / 2 },
    // average of half-width/half-height absorbs sub-pixel rounding rather
    // than picking one axis arbitrarily (same choice the owner's ruler makes).
    radius: ((maxX - minX) / 2 + (maxY - minY) / 2) / 2,
  };
}

// Bound on how many times a caller re-measures + re-corrects a single pill
// (see createPie.js loadRectSize / syncLabelSelectionTransforms and
// renderer-v2/engine.js renderPill). One pass covers the overwhelming
// majority of real cases; a bounded few more handle the rarer case where an
// upstream clamp pulled the box far enough to straddle the wheel's own
// centre, which can change which edge is nearest partway through a single
// push (see ringSafetyCorrection's own test for a worked example). Never
// unbounded — a caller must not be able to loop forever on a pathological
// input (e.g. a box wider than the wheel itself, which no finite push along
// a fixed bisector can ever fully clear).
export const RING_SAFETY_MAX_CORRECTION_PASSES = 6;

// How much FARTHER (>= 0) a real, already-rendered pill rect must move along
// its own outward radial direction so its nearest point clears the ring by
// `requiredGapPx`. Pure geometry (nearest-point-on-axis-aligned-rect-to-a-
// point, same formula the ruler uses) — 0 when the rect already clears.
export function ringSafetyCorrection(pillRect, wedgeCenter, wedgeRadius, requiredGapPx) {
  if (!pillRect || !wedgeCenter || !(wedgeRadius >= 0) || !(requiredGapPx >= 0)) {
    return 0;
  }
  const nearestX = Math.max(pillRect.left, Math.min(wedgeCenter.x, pillRect.right));
  const nearestY = Math.max(pillRect.top, Math.min(wedgeCenter.y, pillRect.bottom));
  const dist = Math.hypot(nearestX - wedgeCenter.x, nearestY - wedgeCenter.y);
  const gap = dist - wedgeRadius;
  const deficit = requiredGapPx - gap;
  return deficit > 0 ? deficit : 0;
}

// batch-proof-fe-dev-ab2970fb (Gordon 2026-10-02): resolveRimPillRadiusBumps
// (geometry.js) only ever pushes a crowded pill's radius OUTWARD to clear a
// pill-pill overlap — it has no notion of the actual browser viewport, so a
// pill already near the 3/9 o'clock meridian (where radius growth moves
// almost entirely horizontally) can be escalated straight past the screen
// edge. MEASURED live on web-dev.iyuga.net (bundle main.ab2970fb.js, the
// FE#6377 fix already live, 390x844 chromium+webkit, a 12-segment reporter-
// shaped wheel reusing FE#6377's own "Exclusive Coupon"/"Spin Again" pair):
// zero pill-pill overlap (the fix works) but 3 of 12 pills landed OFF the
// viewport with no way to scroll to them (document.documentElement.scrollWidth
// === clientWidth === 390, no horizontal overflow exists) — "Exclusive
// Coupon" itself (the exact pill FE#6377's escalation targets) 13px past the
// right edge, "Free Consultation" 36px past the left edge (~52% of the pill
// invisible). The July 2026 "RIM-PILL CLIP FIX" (engine.js render(),
// svg.style.overflow = "visible") deliberately stopped the SVG's own box from
// clipping a pill into the surrounding page whitespace — it says nothing
// about the pill also outrunning the ACTUAL device screen, which has no more
// whitespace past its own edge.
//
// Same shape as ringSafetyCorrection below, but bounding a MAXIMUM extent
// (the real viewport) instead of a minimum radius, and pulling the pill
// INWARD along its own anchor's fixed radial line rather than pushing it out
// — gordon-0915-law's "never hidden, fixed angle" is unchanged (the angle
// never moves, only how far out along it the pill sits), it's simply the
// SAME narrow "radius may be adjusted" exception the law already carves out
// for assignRimPillRadiusTiers/resolveRimPillRadiusBumps, now also bounded on
// the far side. A pill still may overlap a neighbour afterward if pulling it
// fully inside the viewport would mean crossing back under its ring-safety
// minimum — matching the existing "overlap is tolerated, never hiding" law —
// but that is strictly rarer than the viewport being narrower than a single
// pill's own rim gap, which never happens on a real device.
//
// `direction` is the SAME unit vector positionPill's own onCorrect callback
// already derives from the pill's midAngle (ux=sin(angle), uy=-cos(angle)):
// moving by scalar `c` along it shifts a REAL (already-rendered) rect by
// exactly (c*ux, c*uy), since the anchor translate is a pure translation.
export function viewportSafetyCorrection(pillRect, direction, viewportRect) {
  if (!pillRect || !direction || !viewportRect) return 0;
  const { ux, uy } = direction;

  let dxNeeded = 0;
  if (pillRect.right > viewportRect.right) dxNeeded = viewportRect.right - pillRect.right;
  else if (pillRect.left < viewportRect.left) dxNeeded = viewportRect.left - pillRect.left;

  let dyNeeded = 0;
  if (pillRect.bottom > viewportRect.bottom) dyNeeded = viewportRect.bottom - pillRect.bottom;
  else if (pillRect.top < viewportRect.top) dyNeeded = viewportRect.top - pillRect.top;

  if (dxNeeded === 0 && dyNeeded === 0) return 0;

  // Project each axis's required shift onto the fixed direction the pill is
  // actually free to move along; a single scalar correction can't satisfy
  // two independent axis targets at once unless the direction is axis-
  // aligned (the common case here — pills overflow horizontally near 3/9
  // o'clock, vertically near 6/12), so pick whichever axis needs the LARGER
  // magnitude correction — guarantees the worse violation actually clears.
  const candidates = [];
  if (Math.abs(ux) > 1e-6 && dxNeeded !== 0) candidates.push(dxNeeded / ux);
  if (Math.abs(uy) > 1e-6 && dyNeeded !== 0) candidates.push(dyNeeded / uy);
  if (!candidates.length) return 0;
  candidates.sort((a, b) => Math.abs(b) - Math.abs(a));
  return candidates[0];
}

// Same bounded-pass, suspend-the-transition-first shape as
// runRingSafetyCorrectionLoop (see its own header for why the transition
// must be suspended before any getBoundingClientRect() read is trustworthy),
// reused verbatim here so the viewport pull-back can't drift from the ring
// push-out's own measured behaviour. `onCorrect` receives a SIGNED
// correction (negative = pull inward) — callers already apply it with the
// same `anchor.x += correction*ux; anchor.y += correction*uy` math the ring
// loop uses, since viewportSafetyCorrection returns a value in that same
// convention.
export const VIEWPORT_SAFETY_MAX_CORRECTION_PASSES = 4;

export function runViewportSafetyCorrectionLoop({
  measureEl,
  transitionEl,
  direction,
  viewportRect,
  onCorrect,
}) {
  if (!measureEl || typeof measureEl.getBoundingClientRect !== "function") return 0;
  if (!onCorrect || !direction || !viewportRect) return 0;

  const hadTransitionEl = transitionEl && transitionEl.style;
  const prevTransition = hadTransitionEl ? transitionEl.style.transition : undefined;
  if (hadTransitionEl) {
    transitionEl.style.transition = "none";
    transitionEl.getBoundingClientRect();
  }
  let total = 0;
  try {
    for (let i = 0; i < VIEWPORT_SAFETY_MAX_CORRECTION_PASSES; i++) {
      const realRect = measureEl.getBoundingClientRect();
      const correction = viewportSafetyCorrection(realRect, direction, viewportRect);
      if (!correction) break;
      onCorrect(correction);
      total += correction;
    }
  } finally {
    if (hadTransitionEl) transitionEl.style.transition = prevTransition;
  }
  return total;
}

// T22.373 (Kelly audit, View Wheel /wheel/509246, 59 segments, 390x844):
// MEASURED live web-stg main.a47f30e0 / main.156240f3 — the phone wheel sits
// low on the page (ring bottom y=884 > innerHeight 844), so positionPill's
// viewport pull treated every 6 o'clock pill below the FOLD as "off screen"
// and pulled it inward along its radial line, ONTO the disc (3 pills left on
// the wedges, 18 clockwise order inversions, a 4-30deg pill pulled up to
// 32deg off its wedge once the stack resolved the crowd it made). The View
// Wheel page scrolls vertically (its content pane is overflow-y: scroll), so
// a pill below the fold is reachable — only the horizontal edges are real
// (the original repro above: no horizontal overflow exists). The rect the
// v2 engine bounds a pill by is therefore horizontal-only.
export function scrollingPageViewportRect(win) {
  return { left: 0, top: -Infinity, right: win.innerWidth, bottom: Infinity };
}

// T22.278 (Kelly 2026-10-14, iTOVi wheels in view mode on a phone): MEASURED
// live web-stg main.05b317fe.js, /wheel/507376 + /wheel/507379 (iTOVi-shape
// fixtures) at 390x844: the phone wheel's ring is ~170px wide, which leaves
// ~102px beside it at 3 and 9 o'clock once the rim gap is taken, but a
// 14-char one-line pill (PHONE_LABEL_LIMIT, T22.188) is 106-119px wide. The
// viewport pull-back above then dragged "Body Fat Perce..." onto the ring
// (left 275 vs ring right 280) and pinned pills flush to x=0 / x=390, which
// shaved their keyline off. These helpers let the engine shorten JUST a pill
// that cannot fit between the ring and the screen edge; every pill that
// already fits keeps the full 14-char budget.
//
// Room kept between a pill and the screen edge so its 1.5px keyline is
// never cut off by the edge.
export const PILL_VIEWPORT_EDGE_GAP_PX = 4;
// Shortest visible title a fit pass may cut a pill down to.
export const PILL_FIT_MIN_CHARS = 4;

// True when a real, already-rendered pill box clears the ring by
// `requiredGapPx` and sits inside `viewportRect` (left/right only; the page
// scrolls vertically). Sub-pixel slack absorbs float residue from the
// correction loops. A zero-width box was never laid out and always fits.
export function pillRectFitsRoom(pillRect, ring, requiredGapPx, viewportRect) {
  // Unmeasurable (jsdom, a hidden container): nothing to fit against.
  if (!pillRect || !(pillRect.right - pillRect.left > 0)) return true;
  const slack = 0.5;
  if (ring && ring.center && ringSafetyCorrection(pillRect, ring.center, ring.radius, requiredGapPx) > slack) {
    return false;
  }
  if (viewportRect) {
    if (pillRect.left < viewportRect.left - slack) return false;
    if (pillRect.right > viewportRect.right + slack) return false;
  }
  return true;
}

// review-1008-Frontend-7048: an `n`-char cut of `title` that no OTHER title
// in `otherTitles` could also produce. MEASURED live web-stg main.ff4e60de
// /wheel/508295 @390: the fit pass end-cut two different "Body Fat P..."
// titles (and two "Emotional B...") to the SAME text, and on /wheel/508232
// it cut FE#7074's "Introdu...art Two" back to "Introductio...". Tries the
// plain end cut first, then the same (head, tail) splits as
// disambiguateMiddleTruncation (so it reads like FE#7074's pills) — always
// `n` visible chars, so the pill never gets wider than the plain cut. A split is ambiguous when another title
// starts with the same head AND ends with the same tail. Falls back to the
// plain end cut when no split tells the titles apart.
export function distinctEndTruncation(title, n, otherTitles = []) {
  const s = title == null ? "" : String(title);
  const units = graphemeUnits(s);
  const max = Math.max(1, Math.floor(n) || 1);
  if (units.length <= max) return s;
  const others = (otherTitles || [])
    .filter((o) => o != null && String(o) !== s)
    .map((o) => graphemeUnits(String(o)));
  const same = (a, b) => a.length === b.length && a.every((u, i) => u === b[i]);
  const ambiguous = (head, tail) =>
    others.some(
      (o) =>
        o.length >= head + tail &&
        same(o.slice(0, head), units.slice(0, head)) &&
        (tail === 0 || same(o.slice(o.length - tail), units.slice(units.length - tail)))
    );
  if (!ambiguous(max, 0)) return truncateLabelEnd(s, max);
  const splits = [[Math.ceil(max / 2), Math.floor(max / 2)]];
  for (let head = max - 1; head >= 0; head--) {
    if (head !== splits[0][0]) splits.push([head, max - head]);
  }
  for (const [head, tail] of splits) {
    if (!ambiguous(head, tail)) {
      return units.slice(0, head).join("") + "..." + units.slice(units.length - tail).join("");
    }
  }
  return truncateLabelEnd(s, max);
}

// Longest end-truncated form of `title` (at most `limit` visible chars, never
// fewer than `minChars`) that `fits(display)` accepts. `fits` renders and
// measures the candidate; it is tried longest-first, so a pill that fits at
// `limit` is returned unchanged. With `otherTitles` (the other pills on the
// wheel) each candidate stays different from theirs (distinctEndTruncation).
export function fitEndTruncatedLabel(
  title,
  limit,
  fits,
  minChars = PILL_FIT_MIN_CHARS,
  otherTitles = null
) {
  const max = Math.max(1, Math.floor(limit) || 1);
  const min = Math.max(1, Math.min(max, Math.floor(minChars) || 1));
  let last = null;
  for (let n = max; n >= min; n--) {
    const display = otherTitles
      ? distinctEndTruncation(title, n, otherTitles)
      : truncateLabelEnd(title, n);
    if (display === last) continue;
    last = display;
    if (fits(display)) return display;
  }
  return last;
}

// dev-0921-P0 (ring-safety loop stacks 6x): every ringSafetyCorrection call
// site loops up to RING_SAFETY_MAX_CORRECTION_PASSES times, re-measuring the
// pill's REAL box via getBoundingClientRect() each pass and pushing it a
// little farther. Both engines also put a CSS `transition: transform
// <SEGMENT_PILL_GROW_DURATION_MS>ms ...` on that SAME pill <g> for the
// hover/select grow motion (engine.js renderPill sets it before this loop
// ever runs; createPie.js's updateLable sets it once per full draw and every
// later loadRectSize()/syncLabelSelectionTransforms() call reuses that same,
// already-transitioned node). A transitioned element does not commit a
// transform change to layout synchronously — reading
// getBoundingClientRect() right after mutating `transform` returns the
// PRE-correction box (measured live: setting a transform and re-reading it
// in the same synchronous block returns the OLD box; the read only reflects
// the new box once >=1 frame has elapsed). Every pass therefore sees the
// SAME stale box, computes the SAME deficit, and pushes again — after
// RING_SAFETY_MAX_CORRECTION_PASSES=6 passes the pill has been shoved
// 6x(wedgeRadius+requiredGapPx) instead of once (measured: View Wheel pills
// 1622px off a 261px wheel, i.e. off the viewport entirely = 6 x (261+8)).
//
// Fix: suspend the transition on the element that carries it for the
// duration of the loop (forces every mutation to commit instantly, so each
// getBoundingClientRect() read is the TRUE current box) and restore it
// afterward so hover/select grow motion is unaffected. One shared loop for
// both engines so neither can drift from the other's fix.
export function runRingSafetyCorrectionLoop({
  measureEl,
  transitionEl,
  wedgeCenter,
  wedgeRadius,
  requiredGapPx,
  onCorrect,
}) {
  if (!measureEl || typeof measureEl.getBoundingClientRect !== "function") return 0;
  if (!onCorrect || !wedgeCenter || !(wedgeRadius >= 0) || !(requiredGapPx >= 0)) return 0;

  const hadTransitionEl = transitionEl && transitionEl.style;
  const prevTransition = hadTransitionEl ? transitionEl.style.transition : undefined;
  if (hadTransitionEl) {
    transitionEl.style.transition = "none";
    // Force a synchronous style/layout flush so "none" is actually committed
    // before the first correction pass measures anything below.
    transitionEl.getBoundingClientRect();
  }
  let total = 0;
  try {
    for (let i = 0; i < RING_SAFETY_MAX_CORRECTION_PASSES; i++) {
      const realRect = measureEl.getBoundingClientRect();
      const correction = ringSafetyCorrection(realRect, wedgeCenter, wedgeRadius, requiredGapPx);
      if (!(correction > 0)) break;
      onCorrect(correction);
      total += correction;
    }
  } finally {
    if (hadTransitionEl) transitionEl.style.transition = prevTransition;
  }
  return total;
}

// ── Alternate-radius NUDGE — REMOVED (ISP-9999) ──────────────────────────────
// The engines used to rescue a colliding full pill by pushing it radially
// OUTWARD along its centroid ray (nudgeOutwardRect / LABEL_NUDGE_MAX_STEPS /
// LABEL_MAX_NUDGE_RESCUES). That traded ALIGNMENT for spacing — the exact
// behaviour Gordon rejected: on a tightly-packed wheel the bottom-cluster pills
// drifted 33–80px off the rim, ending up far from their own segments. The
// resolver now keeps a colliding pill at its segment anchor (canKeepAnchored,
// accepting a bounded overlap) and only degrades to a dot AT THE ANCHOR when a
// neighbour would occlude it — so a pill never leaves its segment. The outward-
// nudge helper and its two step/rescue caps were deleted with their last caller.
// ── PILL RULE A — tight, orderly vertical stack with pole extremum ─────────
// (SINGLE SOURCE OF TRUTH — both wheel engines: createPie.js resolveLabelOverlaps
// / renderer-v2/engine.js resolvePillOverlaps)
//
// stgfix-1009-T418 (Gordon, GORDON-DECISIONS.md, 2026-10-09 16:13+16:20 PT
// relaying Kelly, decided 17:00 PT as "Pill Rule A"): on a crowded wheel pill-pill
// collision now resolves differently from the canKeepAnchored law above —
// Pill Rule A explicitly WINS over the 2026-09-14 never-displaced law for THIS
// decision only:
//   (1) no pill-pill overlap when the rim has room to avoid it;
//   (2) when pills DO need to move to clear a collision, displaced neighbours
//       touch at exactly LABEL_PILL_GAP_PX (0px) — tight and orderly, never a
//       scattered/arbitrary gap (T4.18);
//   (3) on a crowded wheel the pill nearest 12 o'clock is the HIGHEST (smallest
//       y) of every top-half pill, and the pill nearest 6 o'clock is the LOWEST
//       (largest y) of every bottom-half pill — neighbours step away from the
//       pole, never above/below it (T4.19: the live bug had neighbours of the
//       12 o'clock pill sitting ABOVE it).
// The pill's ANGLE (which segment it is anchored to) never changes — only how
// far up/down it sits. This is a VERTICAL-only correction, layered after every
// other placement step (ring-safety, pane clamp, viewport pull-back) has
// already run and the pill is at its real, final, ring-/viewport-safe box.
//
// Rule (3)'s anchor is deliberately chosen by the pill's own SEGMENT ANGLE,
// never by its rendered box — T4.19's actual root cause is that a TALLER
// pill (a 2-line wrapped title) can get pushed to a slightly larger safe
// radius (pillSafeAnchorRadius) than a shorter neighbour, and near 12/6
// o'clock that extra radius is almost pure vertical travel — so the taller
// neighbour's rendered top can creep ABOVE the true 12 o'clock pill's even
// though it is angularly farther from noon. Keying "who is the pole pill"
// off rendered position would just inherit that same bug; keying it off
// angle fixes it by construction, independent of any text-height skew.
//
// Rule (3) never forces ordinary neighbours into a monotonic order: on a
// sparse wheel two pills can legitimately tie (or nearly tie) on angular
// pole-distance while their NATURAL radii correctly differ a lot (a long,
// multi-line title needs a far larger ring-safety push-out than a one-line
// neighbour), and clamping one against the other would override that
// necessary ring-safety placement. Only a real collision, or the pole
// pill itself (below), ever moves.
//
// stgfix-10101843-T419: the push direction is OUTWARD (away from the wheel:
// up in the 12 o'clock half, down in the 6 o'clock half), never inward.
// MEASURED live on web-stg.iyuga.net (main.a1a75c38.js, /builder/507662,
// /builder/507725, /builder/507726, 1440x900): the T4.18 version kept the
// pole pill fixed and pushed its NEIGHBOURS toward the pole's opposite side
// — which, for any pill near 12/6 o'clock, is straight back into the wheel.
// The ring-safety loop that runs right after (the unconditional "titles
// never overlap the wheel" law, which outranks Pill Rule A) then pushed
// every one of them straight back out, re-creating the overlap (Segment 32 x
// Segment 1 = 392px^2 even with the stack re-applied) and leaving
// neighbours level with / beyond the pole pill. Stacking outward can never
// fight ring-safety, so it is the only direction that can actually hold:
// pills are placed from the half's equator toward its pole, and each pill
// that collides with an already-placed (angularly farther) one is pushed
// outward just far enough to touch it at `gap`. The pole pill is therefore
// placed LAST and lands on top of the pyramid — rule (3) by construction.
//
// resolvePoleStack is the pure 1-D engine for ONE half (top or bottom). `pills`
// must already carry `poleDistance` (>= 0, smaller = angularly closer to this
// half's pole) and `awayFromPoleSign` (+1 = 12 o'clock half, -1 = 6 o'clock
// half; outward is the OPPOSITE sign) — see resolveTightPillStack below.
// `minTop`/`maxBottom` are the real room the pills have (the visible canvas):
// when the full stack would run past them, every displacement in the half is
// scaled down by the SAME factor so the stack just fits — the order (pole
// pill outermost, neighbours stepping away from it) is kept and the overlap
// that remains is spread evenly instead of piling the top two pills onto
// each other (Pill Rule A: "overlap only when there is truly no room").
//
// Rule (3) also holds when the pole pill does NOT collide with anything: its
// neighbours' boxes sit at a larger safe radius (pillSafeAnchorRadius's
// corner term), so near 12/6 o'clock they can naturally sit a pixel or so
// beyond the pole pill (MEASURED: /builder/507726, Segment 13 bottom 733.5 vs
// Segment 12/14 734.4). The pole pill is therefore clamped outward to at
// least the half's extreme among the OTHER pills. When two pills tie on angle
// (12/6 o'clock is a segment boundary) only the tie-break winner is clamped;
// the twin stacks like an ordinary pill (stgfix-10111425-T419). Only the pole
// pill is clamped — never an ordinary neighbour —
// so two angularly-tied, non-colliding pills elsewhere are never forced
// into a monotonic order (see the ring-safety side-pane regression test).
const POLE_TIE_EPSILON_RAD = 1e-9;

// stgfix-10120932-rimpills-symmetry (Gordon 2026-10-12, wheel 507342, 33
// segments, "CLEAN over exact angle"): when TWO pills compete for the pole
// spot — the pills nearest the pole on either side of the axis, at about the
// same angular distance (12/6 o'clock falls on or near the boundary between
// them) — they sit SIDE BY SIDE at the SAME height, split evenly left/right
// of the axis, instead of one stacked above the other (MEASURED web-stg
// main.36da05d4, /builder/508018 @1440: Segment 1 top 72 vs Segment 32 top
// 92). Needs `axisX` (the wheel's vertical axis in page px); without it the
// half stacks exactly as before. Each pair pill then carries `dx` as well.
const POLE_PAIR_MIN_RATIO = 1 / 3;

function sideOfAxis(p) {
  if (!Number.isFinite(p.midAngle)) return 0;
  const s = Math.sin(p.midAngle);
  return Math.abs(s) < 1e-9 ? 0 : Math.sign(s);
}

function findPolePair(valid) {
  if (valid.length < 2) return null;
  const nearest = (side) =>
    valid
      .filter((p) => sideOfAxis(p) === side)
      .reduce((best, p) => (!best || p.poleDistance < best.poleDistance ? p : best), null);
  const left = nearest(-1);
  const right = nearest(1);
  if (!left || !right) return null;
  const minAll = Math.min(...valid.map((p) => p.poleDistance));
  const lo = Math.min(left.poleDistance, right.poleDistance);
  const hi = Math.max(left.poleDistance, right.poleDistance);
  // A pill sitting closer to the pole than both (e.g. 6 o'clock in the middle
  // of a wedge) owns the pole alone; one side clearly nearer = no contest.
  if (minAll < lo - POLE_TIE_EPSILON_RAD) return null;
  if (!(hi > 0) || lo / hi < POLE_PAIR_MIN_RATIO) return null;
  // They only COMPETE when their natural boxes share a column (one would
  // have to stack on the other); two pills that already sit apart (a sparse
  // wheel) keep their own natural places.
  if (!(left.left < right.right && left.right > right.left)) return null;
  return { left, right };
}

// T22.467 (Gordon 2026-10-20, Edit Wheel /builder/507342, 33 equal segments,
// 1920x1080: "Segment 18 should be lined up alongside Segment 16"). Two pills
// at the SAME angular distance from the pole, one each side of the axis
// (Segment 16 and 18 around 6 o'clock), are mirror twins: they belong on ONE
// row. Their natural boxes overlapped by a 1.6px X sliver (87px pills 42.6px
// apart), so the stack pushed whichever twin it placed second a whole pill
// height further out — 18 sat a row below 16 (screenshot: 16 y852, 18 y878)
// and which twin lost depended only on the input order. A sliver that small
// is cleared sideways instead: each twin moves half the sliver away from the
// other (touching at `gap`), so the stack places them side by side. A real
// overlap (more than MIRROR_TWIN_MAX_SLIVER of the narrower pill) still
// stacks as before. The pole pair (findPolePair) is not touched.
const MIRROR_TWIN_TOL_RAD = 1e-6;
const MIRROR_TWIN_MAX_SLIVER = 0.25;

function spreadMirrorTwins(valid, pair, gap) {
  const inPair = (p) => !!pair && (p === pair.left || p === pair.right);
  const used = new Set();
  for (const a of valid) {
    if (inPair(a) || used.has(a) || sideOfAxis(a) !== -1) continue;
    const b = valid.find(
      (q) =>
        !inPair(q) &&
        !used.has(q) &&
        sideOfAxis(q) === 1 &&
        Math.abs(q.poleDistance - a.poleDistance) <= MIRROR_TWIN_TOL_RAD
    );
    if (!b) continue;
    const overlapX = Math.min(a.right, b.right) - Math.max(a.left, b.left);
    const narrower = Math.min(a.right - a.left, b.right - b.left);
    if (!(overlapX > 0) || overlapX > MIRROR_TWIN_MAX_SLIVER * narrower) continue;
    if (!(a.top < b.bottom && a.bottom > b.top)) continue;
    // a is left of the axis, b right of it: meet in the middle of the sliver.
    const mid = (Math.max(a.left, b.left) + Math.min(a.right, b.right)) / 2;
    const shiftA = a.right - (mid - gap / 2);
    const shiftB = mid + gap / 2 - b.left;
    a.left -= shiftA;
    a.right = mid - gap / 2;
    a.dx = (a.dx || 0) - shiftA;
    b.left = mid + gap / 2;
    b.right += shiftB;
    b.dx = (b.dx || 0) + shiftB;
    used.add(a);
    used.add(b);
  }
}

export function resolvePoleStack(
  pills,
  { gap = LABEL_PILL_GAP_PX, minTop = -Infinity, maxBottom = Infinity, axisX } = {}
) {
  const valid = (pills || [])
    .filter(
      (p) =>
        p &&
        Number.isFinite(p.left) &&
        Number.isFinite(p.right) &&
        Number.isFinite(p.top) &&
        Number.isFinite(p.bottom) &&
        Number.isFinite(p.poleDistance) &&
        (p.awayFromPoleSign === 1 || p.awayFromPoleSign === -1)
    )
    .map((p, idx) => ({ ...p, _order: idx }));
  if (!valid.length) return [];

  // Equator first, pole last. On an exact angular tie the EARLIER pill (in
  // clockwise/DOM order) is placed last, so it ends up outermost.
  const pair = Number.isFinite(axisX) ? findPolePair(valid) : null;
  if (Number.isFinite(axisX)) spreadMirrorTwins(valid, pair, gap);
  const sorted = [...valid]
    .filter((p) => !pair || (p !== pair.left && p !== pair.right))
    .sort((a, b) => {
      const d = b.poleDistance - a.poleDistance;
      if (Math.abs(d) > POLE_TIE_EPSILON_RAD) return d;
      return b._order - a._order;
    });
  const pairActive = !!pair;
  const minPoleDistance = sorted.length ? sorted[sorted.length - 1].poleDistance : 0;
  const isPole = (p) => p.poleDistance - minPoleDistance <= POLE_TIE_EPSILON_RAD;
  // stgfix-10111425-T419: THE pole pill — the tie-break winner, placed last.
  // With a pole PAIR the pair owns the pole; nobody else is clamped.
  const primaryPole = pairActive ? null : sorted[sorted.length - 1];

  const topHalf = valid[0].awayFromPoleSign === 1;
  const placed = [];
  for (const p of sorted) {
    const height = p.bottom - p.top;
    const naturalCenter = (p.top + p.bottom) / 2;
    let center = naturalCenter;

    if (p === primaryPole) {
      // stgfix-10111425-T419 (MEASURED web-stg main.0636c6a5.js, /wheel/507662,
      // 32 equal segments, 1440x900): 12 o'clock is the BOUNDARY between
      // Segment 1 and the last segment — an exact angular tie. Clamping BOTH
      // tied pills lifted the twin (the last segment) level with, then above,
      // its own neighbour Segment 28 (top 58), while Segment 1 was only
      // clamped against non-pole pills and stayed at 83: the twin sat 25px
      // ABOVE the 12 o'clock pill. Only THE pole pill — the tie-break winner,
      // placed last — is clamped; a tied twin stacks like any other pill. The
      // winner also clears a tied twin it shares a column with (X-overlap); a
      // twin in another column is left alone (see the non-colliding
      // regression test).
      const others = placed.filter(
        (q) => !isPole(q) || (p.left < q.right && p.right > q.left)
      );
      if (others.length) {
        if (topHalf) {
          const highestTop = Math.min(...others.map((q) => q.top));
          center = Math.min(center, highestTop + height / 2);
        } else {
          const lowestBottom = Math.max(...others.map((q) => q.bottom));
          center = Math.max(center, lowestBottom - height / 2);
        }
      }
    }

    // Rules (1)/(2): push outward until clear of every already-placed pill
    // this one genuinely intersects, touching at exactly `gap`. Each push
    // only ever moves further outward, so this terminates; the pass bound is
    // just a belt-and-braces cap.
    for (let pass = 0; pass <= placed.length; pass++) {
      let moved = false;
      for (const q of placed) {
        const top = center - height / 2;
        const bottom = center + height / 2;
        const xOverlaps = p.left < q.right && p.right > q.left;
        const yOverlaps = top < q.bottom + gap && bottom > q.top - gap;
        if (!xOverlaps || !yOverlaps) continue;
        const target = topHalf ? q.top - gap - height / 2 : q.bottom + gap + height / 2;
        if (topHalf ? target < center : target > center) {
          center = target;
          moved = true;
        }
      }
      if (!moved) break;
    }

    placed.push({
      ...p,
      top: center - height / 2,
      bottom: center + height / 2,
      dy: center - naturalCenter,
    });
  }
  if (pairActive) placed.push(...placePolePair(pair, placed, { gap, axisX, topHalf }));
  // Fit the stack into the room: one shared scale factor for the half. A
  // pill whose NATURAL box is already outside the room is left where it is
  // (never moved further out, never pulled in) and doesn't shrink the rest.
  const outsideRoom = (p) =>
    topHalf ? p.top - p.dy < minTop : p.bottom - p.dy > maxBottom;
  let scale = 1;
  for (const p of placed) {
    if (!p.dy || outsideRoom(p)) continue;
    if (topHalf && p.top < minTop) {
      scale = Math.min(scale, (p.top - p.dy - minTop) / -p.dy);
    } else if (!topHalf && p.bottom > maxBottom) {
      scale = Math.min(scale, (maxBottom - (p.bottom - p.dy)) / p.dy);
    }
  }
  let fitted = placed.map((p) => {
    if (!p.dy) return p;
    const dy = outsideRoom(p) ? 0 : p.dy * scale;
    if (dy === p.dy) return p;
    return { ...p, top: p.top - p.dy + dy, bottom: p.bottom - p.dy + dy, dy };
  });
  if (pairActive) {
    // The shared scale can split the pair (each has its own natural Y): put
    // both back on ONE line — the more outward of the two, kept in the room.
    const twins = fitted.filter((p) => p._polePair);
    const centers = twins.map((p) => (p.top + p.bottom) / 2);
    let line = topHalf ? Math.min(...centers) : Math.max(...centers);
    const h = Math.max(...twins.map((p) => p.bottom - p.top));
    if (topHalf && Number.isFinite(minTop)) line = Math.max(line, minTop + h / 2);
    if (!topHalf && Number.isFinite(maxBottom)) line = Math.min(line, maxBottom - h / 2);
    fitted = fitted.map((p) => {
      if (!p._polePair) return p;
      const delta = line - (p.top + p.bottom) / 2;
      return { ...p, top: p.top + delta, bottom: p.bottom + delta, dy: p.dy + delta };
    });
  }

  return fitted
    .sort((a, b) => a._order - b._order)
    .map(({ _order, _polePair, ...rest }) => rest);
}

// The pole pair: centres mirrored about axisX (equal |centre - axis|), as
// close as the wider pill allows (touching at `gap` when the widths match),
// ONE shared centre line,
// pushed outward clear of every pill already placed, and — the pole rule —
// at least as far out as the half's outermost other pill.
function placePolePair(pair, placed, { gap, axisX, topHalf }) {
  const twins = [pair.left, pair.right];
  const cx = (p) => (p.left + p.right) / 2;
  const half = Math.max(...twins.map((p) => p.right - p.left)) / 2 + gap / 2;
  const off = half;
  const height = Math.max(...twins.map((p) => p.bottom - p.top));
  const boxes = twins.map((p, i) => {
    const w = p.right - p.left;
    const c = axisX + (i === 0 ? -off : off);
    return { p, left: c - w / 2, right: c + w / 2, dx: c - cx(p), h: p.bottom - p.top };
  });
  const naturalCenters = twins.map((p) => (p.top + p.bottom) / 2);
  let center = topHalf ? Math.min(...naturalCenters) : Math.max(...naturalCenters);
  if (placed.length) {
    center = topHalf
      ? Math.min(center, Math.min(...placed.map((q) => q.top)) + height / 2)
      : Math.max(center, Math.max(...placed.map((q) => q.bottom)) - height / 2);
  }
  for (let pass = 0; pass <= placed.length * 2; pass++) {
    let moved = false;
    for (const b of boxes) {
      for (const q of placed) {
        const top = center - height / 2;
        const bottom = center + height / 2;
        if (!(b.left < q.right && b.right > q.left)) continue;
        if (!(top < q.bottom + gap && bottom > q.top - gap)) continue;
        const target = topHalf ? q.top - gap - height / 2 : q.bottom + gap + height / 2;
        if (topHalf ? target < center : target > center) {
          center = target;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  return boxes.map(({ p, left, right, dx, h }) => ({
    ...p,
    left,
    right,
    top: center - h / 2,
    bottom: center + h / 2,
    dy: center - (p.top + p.bottom) / 2,
    dx,
    _polePair: true,
  }));
}

// Normalizes an angle (radians) to (-PI, PI].
function normalizeAngleRad(angle) {
  let a = angle % (2 * Math.PI);
  if (a > Math.PI) a -= 2 * Math.PI;
  if (a <= -Math.PI) a += 2 * Math.PI;
  return a;
}

// Classifies a full set of REAL, already-rendered pill rects into the 12
// o'clock half (pole = 12 o'clock / angle 0) and the 6 o'clock half (pole =
// 6 o'clock / angle +-PI) purely by each pill's OWN `midAngle` (radians, 0 =
// 12 o'clock, clockwise positive — the same convention pillSafeAnchorRadius
// already uses), THEN resolves each half independently with resolvePoleStack.
// Classifying by angle (not by rendered Y) is what makes rule (3) immune to
// the text-height skew described above. Pure; returns a flat array (order
// not significant — callers key off `id`) where every entry carries `dy`,
// the vertical page-pixel delta to apply.
export function resolveTightPillStack(
  pills,
  { gap = LABEL_PILL_GAP_PX, minTop = -Infinity, maxBottom = Infinity, axisX } = {}
) {
  const top = [];
  const bottom = [];
  for (const p of pills || []) {
    if (!p || !Number.isFinite(p.top) || !Number.isFinite(p.bottom) || !Number.isFinite(p.midAngle)) {
      continue;
    }
    const normalized = normalizeAngleRad(p.midAngle);
    const distFromNoon = Math.abs(normalized);
    const isTop = distFromNoon <= Math.PI / 2;
    if (isTop) {
      top.push({ ...p, poleDistance: distFromNoon, awayFromPoleSign: 1 });
    } else {
      bottom.push({ ...p, poleDistance: Math.PI - distFromNoon, awayFromPoleSign: -1 });
    }
  }
  return [
    ...resolvePoleStack(top, { gap, minTop, maxBottom, axisX }),
    ...resolvePoleStack(bottom, { gap, minTop, maxBottom, axisX }),
  ];
}

// audit-10101940-box-editwheel-1 (T4.18 residual): applyTightPillStack's
// vertical stack can be UNDONE by the ring-safety pass that follows it — near
// 12/6 o'clock "away from the pole" is "toward the ring", so ring-safety
// pushes the pill back out along its own angle and re-creates an overlap
// (MEASURED web-stg 507379 @1440: "Total Body Water Percentage" vs
// "Extracellular Water Volume" overlapped 11x19px AFTER the stack). When the
// leftover overlap is a narrow X sliver, shift the pill that sits FARTHER from
// the wheel's vertical axis further AWAY from that axis by exactly the X
// overlap (0px gap touch). Moving away from the axis never moves a pill toward
// the ring. Refuses a shift that would leave [minX, maxX] (the clip boundary)
// or that exceeds maxShiftFraction (0.75; was 0.5, which left the 32-segment
// wheel's 62%-overlap pairs untouched, audit-10111315-box-editwheel-6) of the
// pill's width (a genuinely stacked column is the vertical resolver's job).
// Each pass makes ONE move, so maxPasses is a move budget: 8 ran out on the
// 32-segment wheel (needs 7+ cumulative moves; audit-10111612-box-editwheel-1,
// web-stg 507662 left 3 pairs overlapping after #6857). Pure; returns [{id, dx}] for pills
// that must move (dx in page px, cumulative over passes).
export function resolveResidualPillOverlaps(
  pills,
  {
    axisX,
    minX = -Infinity,
    maxX = Infinity,
    maxShiftFraction = 0.75,
    maxPasses = 400,
    obstacles = [],
  } = {}
) {
  const eps = 0.5;
  const rects = (pills || [])
    .filter((p) => p && [p.left, p.right, p.top, p.bottom].every(Number.isFinite) && Number.isFinite(axisX))
    .map((p) => ({ id: p.id, left: p.left, right: p.right, top: p.top, bottom: p.bottom, dx: 0 }));
  const sideOf = (p) => ((p.left + p.right) / 2 >= axisX ? 1 : -1);
  const distOf = (p) => Math.abs((p.left + p.right) / 2 - axisX);
  const canMove = (p, ox) => {
    const dir = sideOf(p);
    const shift = ox * dir;
    const w = p.right - p.left;
    if (ox > w * maxShiftFraction) return null;
    if (p.left + shift < minX || p.right + shift > maxX) return null;
    // stgfix-10130814-T22131: never shift a pill under a control painted over
    // the canvas (the Builder's Undo/Redo cluster) it was not already under.
    const hits = (l, r) =>
      (obstacles || []).some(
        (o) => o && l < o.right && r > o.left && p.top < o.bottom && p.bottom > o.top
      );
    if (hits(p.left + shift, p.right + shift) && !hits(p.left, p.right)) return null;
    return shift;
  };
  for (let pass = 0; pass < maxPasses; pass++) {
    let moved = false;
    for (let i = 0; i < rects.length && !moved; i++) {
      for (let j = i + 1; j < rects.length && !moved; j++) {
        const a = rects[i];
        const b = rects[j];
        const ox = Math.min(a.right, b.right) - Math.max(a.left, b.left);
        const oy = Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top);
        if (!(ox > eps && oy > eps)) continue;
        // Prefer the pill farther from the axis (ties: the later pill).
        const order = distOf(a) > distOf(b) ? [a, b] : [b, a];
        for (const p of order) {
          // Only meaningful when the two pills sit on the SAME side — moving
          // away from the axis must separate them.
          const shift = canMove(p, ox);
          if (shift == null) continue;
          p.left += shift;
          p.right += shift;
          p.dx += shift;
          moved = true;
          break;
        }
      }
    }
    if (!moved) break;
  }
  return rects.filter((p) => p.dx).map((p) => ({ id: p.id, dx: p.dx }));
}

// review-1008-Frontend-7018 (T22.131 at narrower canvases; MEASURED live
// web-stg main.9c75b55c.js, /builder/507106, 34 segments): FE#7018's side-
// shift fallback clears the 12 o'clock pair at 1440x900 only. At 1280x800 it
// shifts the 2nd "ISP-9999" pill onto "T103-afterproof" (146x12px) and every
// further side shift would land under the Undo/Redo cluster; at 1024x768 the
// pair stays stacked (151x25px) and T103 sits under the cluster (79x5px).
// There is no room above (the canvas top), so the last resort slides pills
// DOWN the rim instead (UP in the 6 o'clock half): for each pill pair still
// overlapping (or a pill under an obstacle), the pill FARTHER from its pole
// moves away from the pole by just enough to clear, plus — when the ring needs
// it — a step away from the wheel's vertical axis. A candidate must stay in
// the room, clear the ring by requiredGapPx (ringSafetyCorrection), stay off
// every obstacle and off every pill nearer its pole; a pill farther from the
// pole that it lands on is pushed on in a later pass (a cascade that only
// ever moves pills away from their pole, so it terminates). Pure; returns
// [{id, dx, dy}] (page px, cumulative) for the pills that move.
export function resolveRoomlessPillCascade(
  pills,
  {
    axisX,
    minTop = -Infinity,
    maxBottom = Infinity,
    minX = -Infinity,
    maxX = Infinity,
    obstacles = [],
    ring = null,
    requiredGapPx = 0,
    gap = LABEL_PILL_GAP_PX,
    step = 4,
    maxPasses = 200,
  } = {}
) {
  const eps = 0.5;
  if (!Number.isFinite(axisX)) return [];
  const rects = (pills || [])
    .filter(
      (p) => p && [p.left, p.right, p.top, p.bottom, p.midAngle].every(Number.isFinite)
    )
    .map((p, order) => {
      const a = normalizeAngleRad(p.midAngle);
      const topHalf = Math.abs(a) <= Math.PI / 2;
      return {
        id: p.id,
        order,
        midAngle: a,
        left: p.left,
        right: p.right,
        top: p.top,
        bottom: p.bottom,
        // +1 = down (12 o'clock half), -1 = up (6 o'clock half)
        away: topHalf ? 1 : -1,
        poleDistance: topHalf ? Math.abs(a) : Math.PI - Math.abs(a),
        dx: 0,
        dy: 0,
      };
    });
  const blocks = (obstacles || []).filter(
    (o) => o && [o.left, o.right, o.top, o.bottom].every(Number.isFinite)
  );
  const meets = (a, b) =>
    Math.min(a.right, b.right) - Math.max(a.left, b.left) > eps &&
    Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top) > eps;
  const ringOk = (b) =>
    !ring || !ring.center || !(ring.radius >= 0) ||
    !(ringSafetyCorrection(b, ring.center, ring.radius, requiredGapPx) > 0);
  // The pill a conflict moves: the one farther from its pole (ties: later).
  const farther = (a, b) =>
    a.poleDistance - b.poleDistance > POLE_TIE_EPSILON_RAD ||
    (Math.abs(a.poleDistance - b.poleDistance) <= POLE_TIE_EPSILON_RAD && a.order > b.order)
      ? a
      : b;
  const roomOk = (b) =>
    b.top >= minTop - eps &&
    b.bottom <= maxBottom + eps &&
    b.left >= minX - eps &&
    b.right <= maxX + eps &&
    ringOk(b) &&
    !blocks.some((o) => meets(b, o));
  // T22.372: move `p` vertically away from `q` (other half), pushing along the
  // chain of pills ahead of it. Returns the plan (or null when any pill of the
  // chain would leave the room); `skip` pills are never part of the chain.
  const planPush = (p, q, dy, skip) => {
    const dir = dy >= 0 ? 1 : -1;
    const shifted = (r) => ({ left: r.left, right: r.right, top: r.top + dy, bottom: r.bottom + dy });
    const chain = [p];
    for (let i = 0; i < chain.length; i++) {
      const c = chain[i];
      const b = shifted(c);
      for (const r of rects) {
        if (r === q || skip.includes(r) || chain.includes(r)) continue;
        if (dir * (r.top + r.bottom - (c.top + c.bottom)) <= 0) continue;
        if (meets(b, r)) chain.push(r);
      }
    }
    return chain.every((r) => roomOk(shifted(r))) ? { chain, dy } : null;
  };
  const applyPush = (plan) => {
    for (const r of plan.chain) {
      r.top += plan.dy;
      r.bottom += plan.dy;
      r.dy += plan.dy;
    }
  };
  const pushAcrossMeridian = (p, q) => {
    const dir = p.top + p.bottom >= q.top + q.bottom ? 1 : -1;
    const need = Math.max(dir > 0 ? q.bottom + gap - p.top : p.bottom - (q.top - gap), 0);
    // stgfix-10182120 (T22.373 kept with T22.372; live fixture /wheel/509246
    // @390): pushing the mover and the whole chain behind it (FE#7447) slid
    // Segment 15 and the 6 o'clock chain 10.1px down the rim, past Se...23 —
    // 13 inversions where T22.373 allows 12. The pill it hit gives way
    // instead: `q` and its chain step back toward THEIR pole by the same
    // amount (the stacks still meet, order unchanged). MEASURED A/B on live
    // web-stg main.bfe052f0 @390 (61 segments today): WebKit 16 -> 14,
    // Chromium 18 -> 17 inversions; /wheel/507342 360/390 stays at 0. When
    // `q`'s side has no room, the mover takes the push, as before.
    const back = planPush(q, p, -dir * need, []);
    const lands = (r, dy) => ({ left: r.left, right: r.right, top: r.top + dy, bottom: r.bottom + dy });
    // T22.373 (scroll fix, FE#7464): the give-way must not throw a pill of
    // the chain more than 20deg off its own wedge (MEASURED fixture
    // /wheel/509246 @390: S9 v0 -20.5deg) - then the mover takes the push.
    const wedgeDev = (r, dy) => {
      if (!ring || !ring.center) return 0;
      const a = Math.atan2((r.left + r.right) / 2 - ring.center.x, -((r.top + r.bottom) / 2 + dy - ring.center.y));
      const d = Math.abs(((a - r.midAngle + 3 * Math.PI) % (2 * Math.PI)) - Math.PI);
      return (d * 180) / Math.PI;
    };
    const backThrows = back && back.chain.some((r) => wedgeDev(r, back.dy) > 20 && wedgeDev(r, back.dy) > wedgeDev(r, 0));
    if (back && !backThrows && !back.chain.some((r) => r !== q && meets(lands(r, back.dy), p))) {
      applyPush(back);
      return true;
    }
    const plan = planPush(p, q, dir * need, []);
    if (!plan || plan.chain.some((r) => meets(lands(r, plan.dy), q))) return false;
    applyPush(plan);
    return true;
  };
  const stuck = new Set();
  const conflict = () => {
    for (const p of rects) {
      if (stuck.has(p)) continue;
      const o = blocks.find((b) => meets(p, b));
      if (o) return { mover: p, clearOf: o };
    }
    for (let i = 0; i < rects.length; i++) {
      for (let j = i + 1; j < rects.length; j++) {
        if (!meets(rects[i], rects[j])) continue;
        const mover = farther(rects[i], rects[j]);
        if (stuck.has(mover)) continue;
        return { mover, clearOf: mover === rects[i] ? rects[j] : rects[i] };
      }
    }
    return null;
  };
  for (let pass = 0; pass < maxPasses; pass++) {
    const c = conflict();
    if (!c) break;
    const { mover: p, clearOf: q } = c;
    // T22.372 (MEASURED live web-stg main.0083969d.js / main.009ed61e.js,
    // /wheel/507342 WebKit 360x780): each half is stacked from its own pole
    // (resolveTightPillStack), so the two stacks meet at 3/9 o'clock, where
    // the halves' "away" directions point at each other. Segment 9 (92.7deg,
    // 6 o'clock half) overlapped Segment 8 (81.8deg, 12 o'clock half) by
    // 0.51px; moved UP across the meridian into the 12 o'clock crowd and,
    // always the pill farther from its pole, it climbed 178.8px to sit
    // between Segments 4 and 5. A conflict between the two halves instead
    // pushes the mover AWAY from the pill it hit, together with every pill it
    // would land on in that direction, so clockwise order never flips.
    if (q.away != null && q.away !== p.away) {
      if (pushAcrossMeridian(p, q)) continue;
      stuck.add(p);
      continue;
    }
    const h = p.bottom - p.top;
    const w = p.right - p.left;
    const side = (p.left + p.right) / 2 >= axisX ? 1 : -1;
    const need = p.away > 0 ? q.bottom + gap - p.top : p.bottom - (q.top - gap);
    const fits = (b) =>
      b.top >= minTop - eps &&
      b.bottom <= maxBottom + eps &&
      b.left >= minX - eps &&
      b.right <= maxX + eps &&
      ringOk(b) &&
      !blocks.some((o) => meets(b, o)) &&
      // never onto a pill nearer the pole (that pill is already settled)
      // T22.373: nor onto ANY pill nearer its own pole — the other half's
      // settled pills too, or an equator pill climbs that whole column.
      !rects.some((r) => r !== p && r.poleDistance < p.poleDistance && meets(b, r));
    // Smallest total displacement first: dy in [need, need + 2h], dx away
    // from the axis in [0, w].
    let best = null;
    for (let j = 0; j * step <= 2 * h; j++) {
      for (let k = 0; k * step <= w; k++) {
        const dy = p.away * (Math.max(need, 0) + j * step);
        const dx = side * k * step;
        const cost = Math.abs(dy) + Math.abs(dx);
        if (best && cost >= best.cost) continue;
        const b = { left: p.left + dx, right: p.right + dx, top: p.top + dy, bottom: p.bottom + dy };
        if (fits(b)) best = { dx, dy, cost };
      }
    }
    if (!best) {
      stuck.add(p);
      continue;
    }
    p.left += best.dx;
    p.right += best.dx;
    p.top += best.dy;
    p.bottom += best.dy;
    p.dx += best.dx;
    p.dy += best.dy;
  }
  return rects
    .filter((p) => p.dx || p.dy)
    .map((p) => ({ id: p.id, dx: p.dx, dy: p.dy }));
}

// T22.295 (Gordon 2026-10-14): "the layout of the segment titles should always
// attempt to be 'circular' instead of stacked". MEASURED web-stg main.405cea26,
// /wheel/508435 @1440x900 (10 oils, four 1-unit wedges at 12-1 o'clock):
// resolveTightPillStack stacked Yarrow Palm / Lemon Eucalyptus / Arborvitae /
// Black Pepper into one vertical staircase (boxes x901-1103, y82-184, each
// directly under the last) while the margin to their right sat empty.
//
// Before the vertical stack runs, slide a crowd ALONG THE RIM instead: every
// pill keeps hugging the wheel (pillSafeAnchorRadius at its new angle, plus
// whatever extra radius it already had) and the crowd's angles are spread
// just far enough apart that neighbours clear each other by `gap`, with the
// least total angular movement (isotonic regression / pool-adjacent-violators
// on the angle-ordered pills, so clockwise order never changes). A crowd is
// only fanned when it really overlaps, every pill stays within `maxShiftRad`
// of where it was, and every moved box stays inside the room and off canvas
// controls; anything else is left exactly where it was for the vertical stack
// (a wheel too dense for the circle keeps today's layout).
//
// pills: [{ id, left, right, top, bottom, midAngle? }] (page px). Angles are read from
// each box's position around `center`, so a rotated wheel needs nothing extra.
// Pure; returns [{ id, dx, dy }] for the pills that move.
export const RIM_FAN_PILL_GAP_PX = 4;
export const RIM_FAN_MAX_SHIFT_RAD = Math.PI / 4;

export function resolveCircularPillFan(
  pills,
  {
    center,
    radius,
    requiredGapPx = 0,
    gap = RIM_FAN_PILL_GAP_PX,
    maxShiftRad = RIM_FAN_MAX_SHIFT_RAD,
    room = {},
  } = {}
) {
  if (!center || !Number.isFinite(center.x) || !Number.isFinite(center.y) || !(radius > 0)) return [];
  const TAU = 2 * Math.PI;
  const items = (pills || [])
    .filter((p) => p && [p.left, p.right, p.top, p.bottom].every(Number.isFinite) && p.right > p.left && p.bottom > p.top)
    .map((p) => {
      const w = p.right - p.left;
      const h = p.bottom - p.top;
      const cx = (p.left + p.right) / 2;
      const cy = (p.top + p.bottom) / 2;
      const theta = (Math.atan2(cx - center.x, -(cy - center.y)) + TAU) % TAU;
      // Radius beyond the rim-hugging one (a tier bump) is kept as it is.
      const hug = pillSafeAnchorRadius(radius + requiredGapPx, theta, w / 2, h / 2);
      const extra = Math.max(0, Math.hypot(cx - center.x, cy - center.y) - hug);
      const mid = Number.isFinite(p.midAngle) ? p.midAngle : theta;
      return { p, w, h, cx, cy, theta, extra, mid };
    });
  if (items.length < 2) return [];

  const boxAt = (it, theta) => {
    const r = pillSafeAnchorRadius(radius + requiredGapPx, theta, it.w / 2, it.h / 2) + it.extra;
    const cx = center.x + r * Math.sin(theta);
    const cy = center.y - r * Math.cos(theta);
    return { left: cx - it.w / 2, right: cx + it.w / 2, top: cy - it.h / 2, bottom: cy + it.h / 2 };
  };
  const clash = (a, b, g) =>
    a.left < b.right + g && a.right > b.left - g && a.top < b.bottom + g && a.bottom > b.top - g;
  const realOverlap = (a, b) =>
    Math.min(a.right, b.right) - Math.max(a.left, b.left) > 0.5 &&
    Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top) > 0.5;
  // Smallest angular separation that clears a (anticlockwise) and b
  // (clockwise) when they straddle `mid`.
  const sepAt = (a, b, mid) => {
    let lo = 0;
    let hi = Math.PI;
    if (clash(boxAt(a, mid - hi / 2), boxAt(b, mid + hi / 2), gap)) return hi;
    for (let i = 0; i < 30; i++) {
      const m = (lo + hi) / 2;
      if (clash(boxAt(a, mid - m / 2), boxAt(b, mid + m / 2), gap)) lo = m;
      else hi = m;
    }
    return hi;
  };

  // Cut the circle at the widest empty arc, then unwrap to increasing angles.
  items.sort((a, b) => a.theta - b.theta);
  const n = items.length;
  let cut = 0;
  let widest = -1;
  for (let k = 0; k < n; k++) {
    const next = items[(k + 1) % n];
    const span = (next.theta - items[k].theta + TAU) % TAU;
    if (span > widest) {
      widest = span;
      cut = (k + 1) % n;
    }
  }
  const seq = [];
  for (let k = 0; k < n; k++) {
    const it = items[(cut + k) % n];
    let t = it.theta;
    if (seq.length && t < seq[seq.length - 1].t) t += TAU;
    seq.push({ it, t });
  }
  const t = seq.map((s) => s.t);

  // Pool-adjacent-violators: x[k+1] - x[k] >= sep[k], least squares to t.
  let x = t.slice();
  let seps = [];
  let blocks = [];
  for (let iter = 0; iter < 4; iter++) {
    seps = [];
    for (let k = 0; k < n - 1; k++) seps.push(sepAt(seq[k].it, seq[k + 1].it, (x[k] + x[k + 1]) / 2));
    const S = [0];
    for (let k = 0; k < n - 1; k++) S.push(S[k] + seps[k]);
    blocks = [];
    for (let k = 0; k < n; k++) {
      blocks.push({ start: k, end: k, sum: t[k] - S[k], count: 1 });
      while (blocks.length > 1) {
        const b = blocks[blocks.length - 1];
        const a = blocks[blocks.length - 2];
        if (a.sum / a.count <= b.sum / b.count) break;
        blocks.splice(-2, 2, { start: a.start, end: b.end, sum: a.sum + b.sum, count: a.count + b.count });
      }
    }
    x = new Array(n);
    for (const b of blocks) for (let k = b.start; k <= b.end; k++) x[k] = b.sum / b.count + S[k];
  }
  // The crowd has to fit on the circle itself (last pill vs first, across the cut).
  if (x[0] + TAU - x[n - 1] < sepAt(seq[n - 1].it, seq[0].it, (x[n - 1] + x[0] + TAU) / 2) - 1e-6) return [];

  const natural = seq.map((s) => ({ left: s.it.p.left, right: s.it.p.right, top: s.it.p.top, bottom: s.it.p.bottom }));
  const target = seq.map((s, k) => boxAt(s.it, x[k]));
  const obstacles = room.obstacles || [];
  const inRoom = (b, nat) =>
    b.left >= (Number.isFinite(room.minX) ? room.minX : -Infinity) &&
    b.right <= (Number.isFinite(room.maxX) ? room.maxX : Infinity) &&
    b.top >= (Number.isFinite(room.minTop) ? room.minTop : -Infinity) &&
    b.bottom <= (Number.isFinite(room.maxBottom) ? room.maxBottom : Infinity) &&
    !obstacles.some((o) => o && clash(b, o, 0) && !clash(nat, o, 0));
  let accepted = blocks.filter((b) => {
    if (b.end === b.start) return false;
    let crowded = false;
    for (let i = b.start; i <= b.end && !crowded; i++) {
      for (let j = i + 1; j <= b.end && !crowded; j++) crowded = realOverlap(natural[i], natural[j]);
    }
    if (!crowded) return false;
    // A crowd with two touching pills straddling 12 or 6 o'clock holds the pole-pair twins, which
    // rimPills.polePairSymmetry / sharedLayout lay out mirrored about the axis
    // and resting on each other; a fan would pull them apart, so it stays on
    // the vertical stack.
    for (let k = b.start; k < b.end; k++) {
      if (Math.sin(seq[k].it.mid) * Math.sin(seq[k + 1].it.mid) < 0 && clash(natural[k], natural[k + 1], 1.5)) return false;
    }
    for (let k = b.start; k <= b.end; k++) {
      if (Math.abs(x[k] - t[k]) > maxShiftRad) return false;
      if (!inRoom(target[k], natural[k])) return false;
    }
    return true;
  });
  // A fanned crowd must not land on a pill that stays put.
  for (let changed = true; changed; ) {
    changed = false;
    const moved = new Set();
    accepted.forEach((b) => {
      for (let k = b.start; k <= b.end; k++) moved.add(k);
    });
    const finalBox = (k) => (moved.has(k) ? target[k] : natural[k]);
    accepted = accepted.filter((b) => {
      for (let k = b.start; k <= b.end; k++) {
        for (let j = 0; j < n; j++) {
          if (j >= b.start && j <= b.end) continue;
          if (realOverlap(target[k], finalBox(j)) && !realOverlap(natural[k], natural[j])) {
            changed = true;
            return false;
          }
        }
      }
      return true;
    });
  }

  const out = [];
  for (const b of accepted) {
    for (let k = b.start; k <= b.end; k++) {
      const dx = (target[k].left + target[k].right) / 2 - seq[k].it.cx;
      const dy = (target[k].top + target[k].bottom) / 2 - seq[k].it.cy;
      if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) out.push({ id: seq[k].it.p.id, dx, dy });
    }
  }
  return out;
}

// T22.295 + T4.19: a fanned crowd hugs the rim, so a WIDE neighbour a few
// degrees from 12 o'clock can sit a fraction of a pixel above the 12 o'clock
// pill (and likewise below the 6 o'clock one). The pole pill — nearest its
// pole by `midAngle`, the clockwise-first on a tie — is lifted (dropped) to
// the half's outermost line. Pure; returns [{ id, dy }].
export function keepPolePillsOutermost(pills) {
  const valid = (pills || []).filter(
    (p) => p && Number.isFinite(p.midAngle) && Number.isFinite(p.top) && Number.isFinite(p.bottom)
  );
  const out = [];
  for (const topHalf of [true, false]) {
    const half = valid.filter((p) => (Math.abs(normalizeAngleRad(p.midAngle)) <= Math.PI / 2) === topHalf);
    if (half.length < 2) continue;
    const dist = (p) => {
      const d = Math.abs(normalizeAngleRad(p.midAngle));
      return topHalf ? d : Math.PI - d;
    };
    const pole = half.reduce((best, p) => (dist(p) < dist(best) - POLE_TIE_EPSILON_RAD ? p : best));
    const others = half.filter((p) => p !== pole);
    const dy = topHalf
      ? Math.min(0, Math.min(...others.map((p) => p.top)) - pole.top)
      : Math.max(0, Math.max(...others.map((p) => p.bottom)) - pole.bottom);
    if (dy) out.push({ id: pole.id, dy });
  }
  return out;
}

// T22.278 (Gordon rule 2026-10-14 23:44 PT): rim pills ALWAYS follow the
// circle — in a crowd, the NEXT pill in clockwise order sits slightly further
// clockwise along the wheel's CLOCKWISE TANGENT: 12-3 o'clock right + down,
// 3-6 down + LEFT, 6-9 left + up, 9-12 up + right. Never stacked straight
// under the previous pill, never against the clockwise direction. MEASURED
// live web-stg main.1436082b, /wheel/508462 (replica of Kelly's iTOVi Report
// Wheel) @390: Scanner Contac... + Yarrow|Pom straddle 12 o'clock and sit as
// the mirrored pole pair (Yarrow centre x 255), and the next pill, "Lemon
// Eucalypt...", stays at its rim-natural x 238 — 17px LEFT of Yarrow, under it.
//
// The vertical stack already orders the crowd in Y (pole pill outermost), so
// only X is corrected: for every pair of clockwise neighbours whose boxes are
// stacked (share a column, touching vertically), the pair's quadrant gives
// the X direction the later pill must lie in, at least `step` px. The pill
// that fixes the order by moving AWAY from the wheel's vertical axis moves
// (that never brings a pill nearer the ring); if the room or another pill
// blocks it, the other pill moves toward the axis instead, as long as it
// stays clear of the ring. A move that would leave the room or land on a
// pill is refused, so the existing laws (never hidden, never on the wheel,
// no new overlap) win over the tangent; a moved pill keeps `edge` px (the
// fitPillToRoom screen-edge gap) inside [minX, maxX], and is never slid under
// a canvas control (`obstacles`, the room's data-rim-pill-obstacle boxes such
// as the Builder's Undo/Redo cluster) it did not already touch (review of
// FE#7281). Repeats until stable, so a chain of
// 3+ crowded pills cascades along the tangent. Pure; returns [{ id, dx }].
export const RIM_TANGENT_STEP_PX = 8;
export const RIM_TANGENT_EDGE_PX = 4;

export function resolveClockwiseTangentOrder(
  pills,
  {
    center,
    radius,
    requiredGapPx = 0,
    minX = -Infinity,
    maxX = Infinity,
    step = RIM_TANGENT_STEP_PX,
    edge = RIM_TANGENT_EDGE_PX,
    obstacles = [],
  } = {}
) {
  const loX = minX + edge;
  const hiX = maxX - edge;
  if (!center || !Number.isFinite(center.x) || !Number.isFinite(center.y) || !(radius > 0)) return [];
  const TAU = 2 * Math.PI;
  const items = (pills || [])
    .filter(
      (p) =>
        p &&
        Number.isFinite(p.midAngle) &&
        [p.left, p.right, p.top, p.bottom].every(Number.isFinite) &&
        p.right > p.left &&
        p.bottom > p.top
    )
    .map((p) => ({
      id: p.id,
      a: ((p.midAngle % TAU) + TAU) % TAU,
      left: p.left,
      right: p.right,
      top: p.top,
      bottom: p.bottom,
      dx: 0,
    }))
    .sort((p, q) => p.a - q.a);
  const n = items.length;
  if (n < 2) return [];

  const cxOf = (p) => (p.left + p.right) / 2 + p.dx;
  const realOverlap = (a, adx, b, bdx) =>
    Math.min(a.right + adx, b.right + bdx) - Math.max(a.left + adx, b.left + bdx) > 0.5 &&
    Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top) > 0.5;
  // Stacked on the ORIGINAL boxes: share a column and touch vertically.
  const stacked = (a, b) => {
    if (!(a.left < b.right && a.right > b.left)) return false;
    const h = Math.max(a.bottom - a.top, b.bottom - b.top);
    const vGap = Math.max(a.top - b.bottom, b.top - a.bottom);
    return vGap <= h / 2;
  };
  const ringClear = (p, dx) => {
    const l = p.left + p.dx + dx;
    const r = p.right + p.dx + dx;
    const nx = Math.max(l, Math.min(center.x, r));
    const ny = Math.max(p.top, Math.min(center.y, p.bottom));
    return Math.hypot(nx - center.x, ny - center.y) >= radius + requiredGapPx - 0.5;
  };
  const onObstacle = (p, adx, o) =>
    Math.min(p.right + adx, o.right) - Math.max(p.left + adx, o.left) > 0.5 &&
    Math.min(p.bottom, o.bottom) - Math.max(p.top, o.top) > 0.5;
  const canShift = (p, dx) => {
    if (p.left + p.dx + dx < loX - 0.5 || p.right + p.dx + dx > hiX + 0.5) return false;
    if (!ringClear(p, dx)) return false;
    if ((obstacles || []).some((o) => o && onObstacle(p, p.dx + dx, o) && !onObstacle(p, p.dx, o))) return false;
    return items.every(
      (q) => q === p || !realOverlap(p, p.dx + dx, q, q.dx) || realOverlap(p, p.dx, q, q.dx)
    );
  };

  const pairs = [];
  for (let k = 0; k < n; k++) {
    const a = items[k];
    const b = items[(k + 1) % n];
    if (a === b || !stacked(a, b)) continue;
    const span = (b.a - a.a + TAU) % TAU;
    const th = (a.a + span / 2) % TAU;
    const quad = Math.min(3, Math.floor(th / (Math.PI / 2)));
    pairs.push({ a, b, sx: quad === 0 || quad === 3 ? 1 : -1 });
  }
  if (!pairs.length) return [];

  for (let pass = 0; pass < n * 4; pass++) {
    let moved = false;
    for (const { a, b, sx } of pairs) {
      const short = step - (cxOf(b) - cxOf(a)) * sx;
      if (short <= 0.01) continue;
      // b moving by +sx or a by -sx fixes the order; prefer the one that
      // moves away from the vertical axis.
      const bAway = Math.sign(cxOf(b) - center.x || 1) === sx;
      const aAway = Math.sign(cxOf(a) - center.x || 1) === -sx;
      const options = [
        { p: b, dir: sx, away: bAway },
        { p: a, dir: -sx, away: aAway },
      ].sort((u, v) => Number(v.away) - Number(u.away));
      for (const { p, dir } of options) {
        // Full step, else as far as the room allows while still >= 1px.
        let dx = short * dir;
        const roomLeft = dir > 0 ? hiX - (p.right + p.dx) : p.left + p.dx - loX;
        if (Math.abs(dx) > roomLeft) dx = Math.max(0, roomLeft) * dir;
        if ((cxOf(b) - cxOf(a)) * sx + Math.abs(dx) < 1) continue;
        if (!canShift(p, dx)) continue;
        p.dx += dx;
        moved = true;
        break;
      }
    }
    if (!moved) break;
  }
  return items.filter((p) => Math.abs(p.dx) > 0.01).map((p) => ({ id: p.id, dx: p.dx }));
}

// T22.432 (Gordon 2026-10-19, prod /builder/896121?pane=4, ~905px window, 33
// segments with long titles): "the pills are not next to their segments".
// MEASURED web-stg main.dcbe2e54, replica /builder/509803 @905x530: a crowd
// too tall for the Builder canvas leaves resolveTightPillStack's room scale
// with overlaps, and the residual X / roomless cascade passes then slide 24 of
// 33 pills up to 38deg round the rim (the nearest point of the pill to the
// wheel, not its centre).
//
// The side-column fit: each half of the wheel (12->6 o'clock on the right,
// 6->12 on the left) is ONE column in clockwise order. Every pill keeps the
// column order and touches its neighbour at `gap`; the column's vertical
// positions are the least-squares fit to the pills' natural heights inside
// [minTop, maxBottom] (pool-adjacent-violators on centre - cumulative
// spacing, clamped to the room), so the displacement is shared by the whole
// side instead of being pushed onto the pills nearest the poles. Each pill
// then sits just clear of the ring (requiredGapPx) at its new height, or at
// its own natural x when that is further out. A column taller than the room
// shares the shortfall evenly (neighbours overlap by the same few px) unless
// that would cover more than `maxSqueeze` of a pill — then [] (too dense).
// [] too when a placed pill would land on a canvas control it was not
// already under. Pure; pills [{ id, midAngle, left, right, top, bottom }]
// (page px, natural ring-safe boxes); returns [{ id, dx, dy }] from those
// boxes.
export const SIDE_COLUMN_MAX_SQUEEZE = 0.2;

export function resolveSideColumnFit(
  pills,
  {
    center,
    radius,
    requiredGapPx = 0,
    gap = LABEL_PILL_GAP_PX,
    minTop = -Infinity,
    maxBottom = Infinity,
    minX = -Infinity,
    maxX = Infinity,
    obstacles = [],
    maxSqueeze = SIDE_COLUMN_MAX_SQUEEZE,
  } = {}
) {
  if (!center || !Number.isFinite(center.x) || !Number.isFinite(center.y) || !(radius > 0)) return [];
  const TAU = 2 * Math.PI;
  const valid = (pills || []).filter(
    (p) =>
      p &&
      Number.isFinite(p.midAngle) &&
      [p.left, p.right, p.top, p.bottom].every(Number.isFinite) &&
      p.right > p.left &&
      p.bottom > p.top
  );
  if (valid.length < 2) return [];
  const clearR = radius + requiredGapPx;
  const angle = (p) => ((p.midAngle % TAU) + TAU) % TAU;
  const right = valid.filter((p) => angle(p) < Math.PI).sort((a, b) => angle(a) - angle(b));
  const left = valid.filter((p) => angle(p) >= Math.PI).sort((a, b) => angle(b) - angle(a));
  const hits = (a, b) => a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
  const out = [];
  for (const [col, side] of [
    [right, 1],
    [left, -1],
  ]) {
    const n = col.length;
    if (!n) continue;
    const h = col.map((p) => p.bottom - p.top);
    const natural = col.map((p) => (p.top + p.bottom) / 2);
    const spacing = [0];
    for (let k = 1; k < n; k++) spacing.push(spacing[k - 1] + (h[k - 1] + h[k]) / 2 + gap);
    const lo = Number.isFinite(minTop) ? minTop + h[0] / 2 : -Infinity;
    let hi = Number.isFinite(maxBottom) ? maxBottom - h[n - 1] / 2 - spacing[n - 1] : Infinity;
    if (lo > hi) {
      const fit = (maxBottom - h[n - 1] / 2 - lo) / spacing[n - 1];
      const squeeze = Math.max(...h.slice(1).map((hk, k) => ((1 - fit) * ((h[k] + hk) / 2 + gap)) / Math.min(h[k], hk)));
      if (!(fit > 0) || squeeze > maxSqueeze) return [];
      for (let k = 0; k < n; k++) spacing[k] *= fit;
      hi = lo;
    }
    const blocks = [];
    for (let k = 0; k < n; k++) {
      blocks.push({ start: k, end: k, sum: natural[k] - spacing[k], count: 1 });
      while (blocks.length > 1) {
        const b = blocks[blocks.length - 1];
        const a = blocks[blocks.length - 2];
        if (a.sum / a.count <= b.sum / b.count) break;
        blocks.splice(-2, 2, { start: a.start, end: b.end, sum: a.sum + b.sum, count: a.count + b.count });
      }
    }
    for (const b of blocks) {
      const base = Math.min(hi, Math.max(lo, b.sum / b.count));
      for (let k = b.start; k <= b.end; k++) {
        const p = col[k];
        const w = p.right - p.left;
        const top = base + spacing[k] - h[k] / 2;
        const bottom = top + h[k];
        const dyRing = top > center.y ? top - center.y : bottom < center.y ? center.y - bottom : 0;
        const ringX = dyRing >= clearR ? 0 : Math.sqrt(clearR * clearR - dyRing * dyRing);
        const naturalX = side > 0 ? p.left - center.x : center.x - p.right;
        const inner = Math.max(ringX, naturalX);
        let l = side > 0 ? center.x + inner : center.x - inner - w;
        if (l + w > maxX) l = maxX - w;
        if (l < minX) l = minX;
        const box = { left: l, right: l + w, top, bottom };
        // Pinned back inside the room onto the ring, or onto a canvas control:
        // this crowd is not ours to place.
        const nx = Math.max(l, Math.min(center.x, l + w));
        const ny = Math.max(top, Math.min(center.y, bottom));
        if (Math.hypot(nx - center.x, ny - center.y) < radius - 0.5) return [];
        if ((obstacles || []).some((o) => o && hits(box, o) && !hits(p, o))) return [];
        out.push({ id: p.id, dx: l - p.left, dy: top - p.top });
      }
    }
  }
  return out;
}
